import { Controller } from "@hotwired/stimulus"
import Konva from "konva"
import Swal from "sweetalert2"
import { partySetupRequest, signInForPartySetup, SETUP_SIGN_IN_MESSAGE } from "./support/party_setup_request"
// import { ensureRandomSampleFont } from "fonts_loader"
import { ensureFontLoaded } from "../fonts_loader";

// Logical size (kept constant) and responsive scale to fit container
const BASE_W = 600
const BASE_H = 800

function sampleWhenString() {
    const d = new Date();
    d.setMonth(d.getMonth() + 1);                 // handles month rollovers

    const datePart = new Intl.DateTimeFormat('en-US', {
        weekday: 'long',
        month:   'short',
        day:     '2-digit',
        year:    'numeric'
    }).format(d);                    // "THURSDAY, NOV 20, 2025"

    return `${datePart}`;               // keep your fixed time
}
function sampleTimeString() {
    return "2:00 PM to 4:00 PM";
}
// Default sample text
const SAMPLE = {
    title: "Bella's 6th Birthday Bash",
    when:  sampleWhenString(),
    time:  sampleTimeString(),
    where: "Our Home · 999 Party Avenue"
};
// Heuristic sampling / masks
const SAMPLE_RES   = 64;       // 64×64 grid over the background
const BUSY_THRESH  = 24;       // edge/variance threshold to mark a cell as “busy”
const TOP_BAND_FR  = 0.16;     // exclude top 16% unless there’s no space
const SAFE_MARGIN  = 0.06;     // 6% margins around the card
const CANDIDATE_STEP = 14;     // px grid step when scanning placements

// Colors we’re allowed to auto-pick from (add or remove to taste)
// Prefer vibrant brand-ish colors first; neutrals last
const AUTO_COLOR_PALETTE = [
    "#1a2d70", // deep blue (brand)
    "#00acc1", // cyan
    "#4caf50", // green
    "#ff4081", // pink
    "#6f42c1", // purple
    "#f44336", // red
    "#ffeb3b", // yellow
    "#333333", // dark gray
    "#ffffff",
    "#000000"
];
// ---- tiny fetch helper with CSRF
function jsonFetch(url, opts = {}) {
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    const { setupRequiresAccount, ...fetchOptions } = opts
    const body = typeof opts.body === 'string' ? JSON.parse(opts.body) : {}
    const setupRequest = url.startsWith('/party_drafts') || (url.startsWith('/cf_images') && body.draft_ref) || /^\/cf_images\/[^/]+\/complete$/.test(url)
    const requiresAccount = setupRequiresAccount || /^\/party_drafts\/\d+(?:$|\/)/.test(url) || /^\d+$/.test(String(body.draft_ref || ''))
    const send = setupRequest ? (path, options) => partySetupRequest(path, options, { requiresAccount }) : fetch
    return send(url, {
        credentials: "same-origin",
        headers: {
            "Content-Type": "application/json",
            "Accept": "application/json",
            ...(token ? { "X-CSRF-Token": token } : {})
        },
        ...fetchOptions
    }).then(async r => {
        const payload = await r.json().catch(() => null)
        if (!r.ok || !payload || r.redirected) {
            const error = new Error(payload?.authentication_required ? SETUP_SIGN_IN_MESSAGE : (payload?.error || `HTTP ${r.status}`))
            error.authenticationRequired = !!payload?.authentication_required
            throw error
        }
        return payload
    })
}

// ---- debounce helper
function debounce(fn, wait = 800) {
    let t
    const debounced = (...args) => {
        clearTimeout(t)
        t = setTimeout(() => fn(...args), wait)
    }
    debounced.cancel = () => clearTimeout(t)
    return debounced
}
function readStickersManifest() {
    const el = document.getElementById('stickers-manifest');
    if (!el) return {};
    try { return JSON.parse(el.textContent || '{}'); } catch { return {}; }
}

const _stickerUrlMap = readStickersManifest();

function resolveStickerUrl(keyOrPath) {
    // already absolute?
    if (/^https?:\/\//.test(keyOrPath)) return keyOrPath;

    // read manifest <script id="stickers-manifest" type="application/json">
    const manifest = window.stickersManifest || (function(){
        try {
            const tag = document.getElementById('stickers-manifest');
            return tag ? JSON.parse(tag.textContent || '{}') : null;
        } catch(_) { return null; }
    })();

    let path = keyOrPath;
    if (!/\.(png|webp|jpg|jpeg)$/i.test(path)) {
        // fill in extension from manifest or default to .png
        const guess = (manifest && manifest[path]) ? manifest[path] : `${path}.png`;
        path = guess;
    }
    // route through pipeline if needed
    if (!/^\/assets\//.test(path)) path = `/assets/${path}`;
    return path;
}
async function directUploadToCloudflare(file, { draftRef, kind }) {
    if (window.__cfDirectUploadPending) return window.__cfDirectUploadPending;
    window.__cfDirectUploadPending = (async () => {
        const { result } = await jsonFetch("/cf_images/direct_upload", {
            method: "POST",
            body: JSON.stringify({ draft_ref: draftRef, kind })
        });
        const fd = new FormData();
        fd.append("file", file);
        const response = await fetch(result.uploadURL, { method: "POST", body: fd });
        if (!response.ok) throw new Error(`Photo upload failed (HTTP ${response.status})`);
        const up = await response.json();
        const imageId = up?.result?.id;
        if (up?.success === false || !imageId) throw new Error("Cloudflare upload failed");

        if (result.private) {
            await jsonFetch(`/cf_images/${encodeURIComponent(imageId)}/complete`, { method: "POST" });
        }

        const HASH = window.CF_DELIVERY_HASH || document.querySelector('meta[name="cf-delivery-hash"]')?.content || "";
        const url = result.deliveryURL || (HASH ? `https://imagedelivery.net/${HASH}/${imageId}/public` : null);
        return { imageId, url };
    })();
    try {
        return await window.__cfDirectUploadPending;
    } finally {
        window.__cfDirectUploadPending = null;
    }
}
export default class extends Controller {
    static targets = ["stageHost", "stageStack", "bgVideo", "photoInput", "jsonField", "emptyState"]
    static values  = {
        bgUrl: String,
        photoEnabled: Boolean,
        draftId: String,
        draftSgid: String,
        anonymous: Boolean,
        initialJson: String,
        context: { type: String, default: "draft" }, // "draft" | "party"
        partyToken: String,
        charityId: Number,
        exclusive: Boolean,
        editorHints: String,
        venuePartnerSlug: String,
        entrySource: String,
    }

    connect() {
        this._editorDisconnected = false
        this._savingFinal = false
        this._navigatingAway = false
        this._saveQueue = Promise.resolve()

        // Build stage/layers
        this.stage = new Konva.Stage({
            container: this.stageHostTarget,
            width: BASE_W, height: BASE_H
        })
        // --- Magic Animation: video background overlay (not a Konva node) ---
        this._onInvitationAnimationCtaChanged = (e) => {
            // Frame navigation or render
            if (e.type === "turbo:frame-load" || e.type === "turbo:frame-render") {
                if (e.target && e.target.id === "invitation_animation_cta") {
                    this._syncVideoBackgroundFromCta()
                }
                return
            }

            // Turbo Stream updates (common)
            if (e.type === "turbo:before-stream-render") {
                const stream = e.target
                if (stream?.getAttribute?.("target") === "invitation_animation_cta") {
                    const originalRender = e.detail?.render
                    if (typeof originalRender === "function") {
                        e.detail.render = (s) => {
                            originalRender(s)
                            // Frame element may have been replaced; rebind observer to the new one
                            this._rebindCtaObserver?.()
                            // Now read the fresh dataset + toggle video/png
                            this._syncVideoBackgroundFromCta()
                        }
                    } else {
                        // Fallback: best-effort
                        queueMicrotask(() => {
                            this._rebindCtaObserver?.()
                            this._syncVideoBackgroundFromCta()
                        })
                    }
                }
            }
        }

        // Sequencer used to cancel stale rAF/timeout callbacks that may re-hide the PNG bg after turning animation off
        this._bgVideoSeq = 0
        this._bgVideoHideTimeoutId = null

        // Also watch for non-Turbo updates to the CTA.
        // IMPORTANT: The turbo-frame itself gets replaced by Turbo Stream `replace`,
        // so we must (re)bind the observer to the *current* frame element.
        this._ctaObserver = new MutationObserver(() => {
            queueMicrotask(() => this._syncVideoBackgroundFromCta())
        })

        this._rebindCtaObserver = () => {
            try { this._ctaObserver.disconnect() } catch (_) {}
            const frame = document.getElementById("invitation_animation_cta")
            if (!frame) return
            this._ctaObserver.observe(frame, {
                childList: true,
                subtree: true,
                attributes: true
            })
        }

        this._rebindCtaObserver()

        document.addEventListener("turbo:frame-load", this._onInvitationAnimationCtaChanged)
        document.addEventListener("turbo:frame-render", this._onInvitationAnimationCtaChanged)
        document.addEventListener("turbo:before-stream-render", this._onInvitationAnimationCtaChanged)

        queueMicrotask(() => this._syncVideoBackgroundFromCta())
        this._lastPersistedAnimationEnabled = null

        // --- Magic Animation: sound preference (muted by default; toggle via CTA) ---
        this._soundEnabled = false
        this._onAnimationSoundToggled = (e) => {
            const detail = e?.detail || {}
            const invId = detail.invitationId
            const enabled = !!detail.enabled
            this._soundEnabled = enabled

            // Apply only if this editor is for the same invitation
            const cta = this._ctaRootEl?.()
            const currentId = cta?.dataset?.invitationAnimationInvitationIdValue
            if (currentId && invId && String(currentId) !== String(invId)) return

            if (!this.hasBgVideoTarget) return
            const v = this.bgVideoTarget
            if (!v) return

            // If we had a pending deferred-unmute handler from earlier, cancel it.
            if (this._unmuteOnGesture) {
                window.removeEventListener("pointerdown", this._unmuteOnGesture, true)
                this._unmuteOnGesture = null
            }
            this._pendingUnmute = false

            if (!enabled) {
                // Mute immediately (property + boolean attribute).
                try { v.muted = true } catch (_) {}
                try { v.setAttribute("muted", "") } catch (_) {}
                return
            }

            // Try to enable audio immediately. Some browsers require a *direct* gesture;
            // but the CTA click should count, so we do the unmute + (re)play in this same call stack.
            // IMPORTANT: remove the boolean `muted` attribute, otherwise Chrome may keep audio blocked.
            try { v.muted = false } catch (_) {}
            try { v.removeAttribute("muted") } catch (_) {}
            try { v.volume = 1 } catch (_) {}

            // Force a play call after unmuting (some browsers won't apply audio to an already-playing element).
            try {
                // If it was already playing, pausing then playing can help apply the new audio state.
                v.pause?.()
            } catch (_) {}

            try {
                const p = v.play?.()
                if (p && typeof p.catch === "function") {
                    p.catch(() => {
                        // If the browser still blocks audio, keep it muted and defer until next gesture.
                        try { v.muted = true } catch (_) {}
                        try { v.setAttribute("muted", "") } catch (_) {}
                        this._deferUnmuteOnNextGesture?.()
                    })
                }
            } catch (_) {
                try { v.muted = true } catch (_) {}
                try { v.setAttribute("muted", "") } catch (_) {}
                this._deferUnmuteOnNextGesture?.()
            }
        }
        window.addEventListener("invitation:animationSoundToggled", this._onAnimationSoundToggled)
        // --- Magic Animation: editor must react when CTA explicitly toggles enabled state.
        // This prevents autosave from later overwriting the server-side fields_json with a stale value.
        this._onAnimationEnabledChanged = (e) => {
            const detail = e?.detail || {}
            const enabled = !!detail.enabled

            // If this event includes an invitationId, ignore if it doesn't match the current CTA invitation id.
            const invId = detail.invitationId
            const cta = this._ctaRootEl?.()
            const currentId = cta?.dataset?.invitationAnimationInvitationIdValue
            if (invId && currentId && String(invId) !== String(currentId)) return

            // Treat this as a real interaction so drafts get created and editor_json gets persisted.
            this._suppressAutosaveUntilInteraction = false

            // Set a short-lived override so that if Turbo stream replaces the CTA with a stale enabled=false,
            // we don't immediately persist the wrong value back to the draft.
            this._animationEnabledOverride = enabled
            this._animationEnabledOverrideAt = Date.now()

            // Persist immediately (this will also ensure editor_json is saved so reload doesn't blank text).
            this._maybePersistAnimationEnabledToDraft?.(enabled)
        }
        window.addEventListener("invitation:animationEnabledChanged", this._onAnimationEnabledChanged)
        if (window) {
            // keep it a bit namespaced to avoid collisions
            window.__ieStage = this.stage;
        }
        this.photoLayer = new Konva.Layer();
        this.bgLayer    = new Konva.Layer()
        this.stickersLayer = new Konva.Layer();
        this.content    = new Konva.Layer()
        this.uiLayer    = new Konva.Layer() // selection handles
        this.stage.add(this.photoLayer, this.bgLayer, this.stickersLayer, this.content, this.uiLayer)

        // Transformer for selected node
        this.transformer = new Konva.Transformer({
            rotateEnabled: true,
            enabledAnchors: ['middle-left','middle-right','top-left','top-right','bottom-left','bottom-right']
        })

        this.content.find("Text").forEach(t => {
            const bw = t.getAttr("_boxWidth") || Math.round(this.stage.width() * 0.86)
            t.width(bw)
            t.setAttr("_boxWidth", bw)
            if (!t.align()) t.align("center")    // default center
            t.offsetX(0)                          // never shift the box by offset
        })
        this.content.draw()

        // bind once so we can remove later
        this._onAlign = (e) => {
            if (!this.selected || this.selected.className !== "Text") return
            const a = e.detail?.align || "center"

            const x = this.selected.x()          // remember position
            const bw = this.selected.getAttr("_boxWidth") || this.selected.width()

            this.selected.align(a)
            this.selected.width(bw)              // keep the same box width
            this.selected.offsetX(0)             // do NOT shift by offset
            this.selected.x(x)                   // keep exact X

            this.content.batchDraw()
            this._emitInspect(this.selected)     // keep panel in sync
            this._syncJson?.()
        }

        this._onCase = (e) => {
            if (!this.selected || this.selected.className !== "Text") return
            const kase = e.detail?.case || "uppercase"
            const raw  = this.selected._rawText || this.selected.text()
            this.selected._rawText = raw
            this.selected.setAttr("_case", kase)

            let t = raw
            if (kase === "uppercase") t = raw.toUpperCase()
            else if (kase === "lowercase") t = raw.toLowerCase()
            else if (kase === "capitalize") t = raw.replace(/\b\w/g, m => m.toUpperCase())

            this.selected.text(t)
            this.content.batchDraw()
            this._emitInspect(this.selected)
            this._syncJson?.()
        }

        this._onAddSticker = (e) => {
            const { src, name } = e.detail || {};
            this._suppressAutosaveUntilInteraction = false;
            if (src) this._addSticker(src, name || "sticker");
        };
        document.addEventListener("ie:addSticker", this._onAddSticker);

        document.addEventListener("ie:align", this._onAlign)
        document.addEventListener("ie:case",  this._onCase)

        // Build floating mini-toolbar (delete button)
        this._buildMiniToolbar();
        // this._onKeydown = (e) => {
        //     if (!this.selected) return;
        //     if (e.key === 'Delete' || e.key === 'Backspace') {
        //         e.preventDefault();
        //         this._deleteSelected();
        //     }
        // };
        // window.addEventListener('keydown', this._onKeydown);

        // this.draftId = null
        this.invitationSlug = this.element?.dataset?.invitationEditorBgUrlValue
            ? (new URL(this.element.dataset.invitationEditorBgUrlValue)).pathname.split("/").pop().replace(".png","")
            : (new URLSearchParams(location.search).get("invitation") || "unknown")

        // autosave debouncer
        this._autosaveJsonDebounced = debounce((payload) => this._autosaveJson(payload), 800)
        // Do not autosave until the user interacts
        this._suppressAutosaveUntilInteraction = true;
        this._markUserInteraction = () => {
            if (!this._initialized || this._savingFinal) return;
            this._suppressAutosaveUntilInteraction = false;
            // Create the draft immediately on first interaction (once)
            if (this.contextValue === "draft" && !this.draftId) {
                this._createOrReuseDraft().catch(e => this._warnAutosaveFailure(e));
            }
        };
// Any pointer action on the canvas counts as interaction (fires once)
        this.stage.container().addEventListener('pointerdown', this._markUserInteraction, { once: true });

        // Create/reuse draft once the stage is ready and initial JSON exists
        // queueMicrotask(() => this._createOrReuseDraft().catch(console.warn))

        this._onContinue = () => this._handleContinue()
        document.addEventListener("ie:continue", this._onContinue)

        // === listen to tool panel events ===
        this._onSetColor = (e) => {
            const hex = e.detail?.hex
            if (!hex) return
            const n = this._active
            const t = this._resolveTextNode?.(n) || n
            if (!t || t.getClassName?.() !== "Text") return

            // force repaint even if Konva had cached the node
            t.clearCache?.()
            t.fill(hex)
            t.getLayer()?.batchDraw()

            // keep side panel in sync
            // this._emitInspect(t)
        }
        this._onSetFontFamily = e => this._applyAndReflect(n => n.fontFamily(e.detail.family));
        this._onSetFontWeight = e => this._applyToActive(n => n.fontStyle(e.detail.weight));
        this._onSetFontSize   = e => this._applyToActive(n => n.fontSize(parseInt(e.detail.size,10)));

        this._onResetColor      = () => this._applyAndReflect(n => n.fill("#1a2d70"))
        this._onResetFontFamily = () => this._applyAndReflect(n => n.fontFamily("Arial"))
        this._onResetFontWeight = () => this._applyAndReflect(n => n.fontStyle("normal"))
        this._onResetFontSize   = () => this._applyAndReflect(n => n.fontSize(24))
        document.getElementById("reset-text-format")?.addEventListener("click", () => {
            this._applyAndReflect(n => {
                n.align("center")
                n.setAttr("_case", "uppercase")
                // normalize visible text to that case right away
                const t = n.text()
                n.text(t.toUpperCase())
            })
        });

        // --- photo tool events ---
        this._onPhotoReplace = () => this.choosePhoto()
        this._onPhotoDelete  = () => this._deletePhoto()
        this._onPhotoSizeChange = (e) => {
            const pct = parseFloat(e.detail?.pct);
            this._setPhotoSizePercent(pct);
            this._syncPhotoUi(); // Sync back to UI to normalize value (e.g., if it was capped)
        };
        this._onPhotoSizeReset = () => {
            this._setPhotoSizePercent(100);
            this._syncPhotoUi();
        };
        this._onPhotoRotateStep = (e) => {
            const currentRotation = this.photoCarrier?.rotation() || 0;
            const newRotation = currentRotation + (e.detail?.delta || 0);
            this._setPhotoRotation(newRotation);
            this._syncPhotoUi();
        };
        this._onPhotoRotateTo = (e) => {
            const deg = parseFloat(e.detail?.deg);
            this._setPhotoRotation(deg);
            this._syncPhotoUi();
        };
        this._onPhotoRotateReset = () => {
            this._setPhotoRotation(0);
            this._syncPhotoUi();
        };
        this._onPhotoFilter = (e) => {
            this._applyPhotoFilter(e.detail?.name || "");
            this._syncPhotoUi();
        };
        this._onPhotoFilterReset = () => {
            this._applyPhotoFilter("");
            this._syncPhotoUi();
        };

        // this._onPhotoAnimPulse  = () => this._startPhotoPulse()
        // this._onPhotoAnimStop   = () => this._stopPhotoPulse()

        document.addEventListener("ie:setColor",      this._onSetColor);
        document.addEventListener("ie:setFontFamily", this._onSetFontFamily);
        document.addEventListener("ie:setFontWeight", this._onSetFontWeight);
        document.addEventListener("ie:setFontSize",   this._onSetFontSize);

        document.addEventListener("ie:resetColor",      this._onResetColor);
        document.addEventListener("ie:resetFontFamily", this._onResetFontFamily);
        document.addEventListener("ie:resetFontWeight", this._onResetFontWeight);
        document.addEventListener("ie:resetFontSize",   this._onResetFontSize);

        document.addEventListener("ie:photoReplace", this._onPhotoReplace)
        document.addEventListener("ie:photoDelete",  this._onPhotoDelete)
        document.addEventListener("ie:photoFilter",  this._onPhotoFilter)
        document.addEventListener("ie:photoFilterReset", this._onPhotoFilterReset)
        // document.addEventListener("ie:photoAnimPulse", this._onPhotoAnimPulse)
        // document.addEventListener("ie:photoAnimStop",  this._onPhotoAnimStop)
        document.addEventListener("ie:photoSizeReset", this._onPhotoSizeReset)
        document.addEventListener("ie:photoSizeChange", this._onPhotoSizeChange)
        document.addEventListener("ie:photoRotateReset", this._onPhotoRotateReset)
        document.addEventListener("ie:photoRotateStep",  this._onPhotoRotateStep)
        document.addEventListener("ie:photoRotateTo",    this._onPhotoRotateTo)

        // Bridge photo-size range → ie:photoSize event (0..200%)
        const sizeEl = document.querySelector("#photo-size")
        if (sizeEl) {
            const emit = () => document.dispatchEvent(new CustomEvent("ie:photoSizeChange", { detail: { pct: parseFloat(sizeEl.value)||100 } }))
            sizeEl.addEventListener("input", emit)
            sizeEl.addEventListener("change", emit)
        }

        this.uiLayer.add(this.transformer)

        // heuristic should run only once for the “blank sample” case
        this._didAutoPlace = false

        // kick off init (no await needed)
        //this._loadInitialDesign(this.bgUrlValue)
        this._initialized = false

        this._initPromise = Promise
            .resolve(this._loadInitialDesign(this.bgUrlValue))
            .then(() => {
                this._initialized = true
                if (this.contextValue !== 'party') window.echoageSetupDiagnostics?.report?.('editor_ready');

                // Now that Konva nodes exist, re-evaluate video-vs-PNG from CTA
                this._syncVideoBackgroundFromCta?.()

                // And only now allow a sync to happen
                try { this._syncJson?.() } catch (_) {}
            })
            .catch((e) => console.warn("Invitation editor init failed", e))

        // If we did auto-place (sample mode), re-run on resize once layout is ready
        window.addEventListener("resize", () => {
            if (!this._didAutoPlace) return
            this._schedule(() => this._applyHintsOrAutoLayout().then(() => this.content.batchDraw()))
        })

        // this._ensureSampleText()

        // Selection
        this.stage.on("click tap", (e) => this._handleStageClick(e))

        // Responsive sizing
        this._resizeObserver = new ResizeObserver(() => this._fit())
        this._resizeObserver.observe(this.stageHostTarget)
        this._fit()

        // Wire left-side controls (minimal bridge; reuses your IDs)
        this._bindControls()

        // If Rails provided a draft id, use it immediately
        if (this.hasDraftIdValue && this.draftIdValue) this.draftId = this.draftIdValue;

        // Optional: also keep the SGID
        if (this.hasDraftSgidValue && this.draftSgidValue) this._draftSgid = this.draftSgidValue;

        // Persist JSON on changes
        this.stage.on("dragend transformend mouseup touchend", () => {
            if (!this._initialized) return
            this._syncJson()
        })

        // Schedule a few redraw passes so Konva picks up webfonts
        if (typeof this._scheduleFontRefresh === "function") {
            this._scheduleFontRefresh();
        }
        // Also hook the browser's font readiness signal
        if (document.fonts?.ready) {
            document.fonts.ready
                .then(() => {
                    if (!this.stage || this.stage.isDestroyed?.()) return;
                    if (typeof this._scheduleFontRefresh === "function") {
                        this._scheduleFontRefresh();
                    }
                })
                .catch(() => {});
        }

    }

    disconnect() {
        this._editorDisconnected = true
        this._finishInlineEdit?.()
        this._autosaveJsonDebounced?.cancel()
        // window.removeEventListener('keydown', this._onKeydown);
        document.removeEventListener("turbo:frame-load", this._onInvitationAnimationCtaChanged)
        document.removeEventListener("turbo:frame-render", this._onInvitationAnimationCtaChanged)
        document.removeEventListener("turbo:before-stream-render", this._onInvitationAnimationCtaChanged)
        this._stopAndHideBgVideo?.()
        this._ctaObserver?.disconnect()
        this._ctaObserver = null
        this._rebindCtaObserver = null
        document.removeEventListener("ie:continue", this._onContinue)
        this._destroyMiniToolbar?.();
        document.removeEventListener("ie:align", this._onAlign)
        document.removeEventListener("ie:case",  this._onCase)
        document.removeEventListener("ie:setColor",      this._onSetColor);
        document.removeEventListener("ie:setFontFamily", this._onSetFontFamily);
        document.removeEventListener("ie:setFontWeight", this._onSetFontWeight);
        document.removeEventListener("ie:setFontSize",   this._onSetFontSize);

        document.removeEventListener("ie:resetColor",      this._onResetColor);
        document.removeEventListener("ie:resetFontFamily", this._onResetFontFamily);
        document.removeEventListener("ie:resetFontWeight", this._onResetFontWeight);
        document.removeEventListener("ie:resetFontSize",   this._onResetFontSize);
        document.removeEventListener("ie:photoReplace", this._onPhotoReplace)
        document.removeEventListener("ie:photoDelete",  this._onPhotoDelete)
        document.removeEventListener("ie:photoFilter",  this._onPhotoFilter)
        document.removeEventListener("ie:photoFilterReset", this._onPhotoFilterReset)
        // document.removeEventListener("ie:photoAnimPulse", this._onPhotoAnimPulse)
        // document.removeEventListener("ie:photoAnimStop",  this._onPhotoAnimStop)
        document.removeEventListener("ie:photoSizeReset", this._onPhotoSizeReset)
        document.removeEventListener("ie:photoRotateReset", this._onPhotoRotateReset)
        document.removeEventListener("ie:photoRotateStep",  this._onPhotoRotateStep)
        document.removeEventListener("ie:photoRotateTo",    this._onPhotoRotateTo)
        document.removeEventListener("ie:addSticker", this._onAddSticker);
        window.removeEventListener("invitation:animationSoundToggled", this._onAnimationSoundToggled)
        window.removeEventListener("invitation:animationEnabledChanged", this._onAnimationEnabledChanged)
        this._onAnimationEnabledChanged = null
        if (this._unmuteOnGesture) {
            window.removeEventListener("pointerdown", this._unmuteOnGesture, true)
            this._unmuteOnGesture = null
        }
        this._pendingUnmute = false
        this._onAnimationSoundToggled = null
        this._resizeObserver?.disconnect()
        this.stage?.destroy()
    }

    _ctaRootEl() {
        // CTA gets replaced via Turbo Streams; always query fresh.
        return document.querySelector('#invitation_animation_cta [data-controller~="invitation-animation"]')
    }

    _syncVideoBackgroundFromCta() {
        if (!this.hasBgVideoTarget) return

        const cta = this._ctaRootEl()
        if (!cta) {
            this._bgVideoSeq = (this._bgVideoSeq || 0) + 1
            if (this._bgVideoHideTimeoutId) {
                try { clearTimeout(this._bgVideoHideTimeoutId) } catch (_) {}
                this._bgVideoHideTimeoutId = null
            }
            this._stopAndHideBgVideo()
            // Restore the Konva PNG background.
            this._setKonvaPngBackgroundVisible(true)
            return
        }

        const videoUrl = cta.dataset.invitationAnimationVideoUrlValue
        const enabledRaw = cta.dataset.invitationAnimationEnabledValue
        const animatedRaw = cta.dataset.invitationAnimationAnimatedValue
        const status = cta.dataset.invitationAnimationStatusValue

        // IMPORTANT:
        // `enabledRaw` can be missing or stale during initial render / Turbo Stream replace.
        // We MUST NOT persist defaults (e.g., nil->false) back to the draft unless the user explicitly toggled.
        const hasExplicitEnabled = !(enabledRaw === undefined || enabledRaw === null || enabledRaw === "")
        const enabledFromDataset = hasExplicitEnabled ? (enabledRaw === "true" || enabledRaw === true) : null
        const animated = (animatedRaw === "true" || animatedRaw === true)

        // Respect a short-lived override coming from the CTA click.
        // Turbo stream replaces can briefly re-render the CTA with a stale enabled value.
        let enabled = (enabledFromDataset === null) ? true : enabledFromDataset
        const overrideActive = this._animationEnabledOverrideAt && (Date.now() - this._animationEnabledOverrideAt) < 15000
        if (overrideActive) {
            enabled = this._animationEnabledOverride
            // If the CTA has caught up to the override, clear it.
            if (enabledFromDataset !== null && enabledFromDataset === this._animationEnabledOverride) {
                this._animationEnabledOverrideAt = null
                this._animationEnabledOverride = null
            }
        } else {
            // Override expired
            this._animationEnabledOverrideAt = null
            this._animationEnabledOverride = null
        }

        // Persist ONLY when the user explicitly toggled (override active).
        // Otherwise we risk saving a stale/default value back to the draft on initial load.
        if (overrideActive) {
            this._maybePersistAnimationEnabledToDraft?.(enabled)
        }

        // treat nil as ok; the job sets ready/failed but be tolerant
        const readyish = !status || status === "ready" || status === "done"
        const shouldShow = !!(videoUrl && animated && enabled && readyish)

        if (!shouldShow) {
            this._bgVideoSeq = (this._bgVideoSeq || 0) + 1
            if (this._bgVideoHideTimeoutId) {
                try { clearTimeout(this._bgVideoHideTimeoutId) } catch (_) {}
                this._bgVideoHideTimeoutId = null
            }
            this._stopAndHideBgVideo()
            this._setKonvaPngBackgroundVisible(true) // defensive restore
            return
        }
        this._setKonvaPngBackgroundVisible(false)
        try {
            requestAnimationFrame(() => this._setKonvaPngBackgroundVisible(false))
        } catch (_) {}
        this._showBgVideo(videoUrl)
    }

    _maybePersistAnimationEnabledToDraft(enabled) {
        // Only drafts need immediate persistence; party mode will persist via server-side party update flows.
        if (this.contextValue !== "draft") return

        // Avoid spamming PATCHes.
        if (this._lastPersistedAnimationEnabled === enabled) return
        this._lastPersistedAnimationEnabled = enabled

        // Fire-and-forget: CTA updates are driven by Turbo stream replaces.
        queueMicrotask(() => this._persistAnimationEnabledToDraft(enabled))
    }

    async _persistAnimationEnabledToDraft(enabled) {
        try {
            if (this.contextValue !== "draft") return

            // Ensure we have a draft id even if the user only toggles animation.
            if (!this.draftId) {
                // Prefer existing helper if present.
                if (typeof this._createOrReuseDraft === "function") {
                    await this._createOrReuseDraft()
                }

                // Fallback: create a draft the same way we do in continue flow.
                if (!this.draftId) {
                    const payload = _normalizeForSave(this._dumpJson())
                    const res = await jsonFetch("/party_drafts", {
                        method: "POST",
                        body: JSON.stringify({
                            invitation_slug: this.invitationSlug,
                            base_width: BASE_W,
                            base_height: BASE_H,
                            editor_json: payload,
                            ...this._partyPlaceDraftContext()
                        })
                    })
                    this._adoptDraftResponse(res)
                }
            }

            if (!this.draftId) return

            // IMPORTANT: If the user only toggles animation (no other edits), we still need to persist
            // the current editor_json (default/sample text, positions, etc.). Otherwise the draft can
            // rehydrate as empty on reload.
            // Treat animation toggle as a real user interaction.
            this._suppressAutosaveUntilInteraction = false

            try {
                const payload = _normalizeForSave(this._dumpJson())
                if (payload && typeof this._autosaveJson === "function") {
                    // Save immediately (no debounce) so refresh right after toggle still rehydrates correctly.
                    await this._autosaveJson(payload)
                } else if (payload && typeof this._autosaveJsonDebounced === "function") {
                    // Fallback: at least queue a save.
                    this._autosaveJsonDebounced(payload)
                }
            } catch (e) {
                console.warn("Failed to persist editor_json on animation toggle", e)
            }

            // Patch only the fields_json party flag. Server should merge this into draft.fields_json.
            await jsonFetch(`/party_drafts/${this.draftId}`,
                {
                    method: "PATCH",
                    body: JSON.stringify({
                        fields_json: {
                            party: {
                                animation_enabled: !!enabled
                            }
                        }
                    })
                }
            )
        } catch (e) {
            console.warn("Failed to persist animation_enabled to draft", e)
        }
    }

    _showBgVideo(url) {
        const v = this.bgVideoTarget
        if (!v) return

// IMPORTANT: schedule defensive passes, but they MUST be cancellable
        const seq = ++this._bgVideoSeq

// cancel any prior pending hide timeout
        if (this._bgVideoHideTimeoutId) {
            try { clearTimeout(this._bgVideoHideTimeoutId) } catch (_) {}
            this._bgVideoHideTimeoutId = null
        }

        this._setKonvaPngBackgroundVisible(false)

        try {
            requestAnimationFrame(() => {
                if (seq !== this._bgVideoSeq) return
                this._setKonvaPngBackgroundVisible(false)
            })
            requestAnimationFrame(() => {
                if (seq !== this._bgVideoSeq) return
                this._setKonvaPngBackgroundVisible(false)
            })
            this._bgVideoHideTimeoutId = setTimeout(() => {
                if (seq !== this._bgVideoSeq) return
                this._setKonvaPngBackgroundVisible(false)
            }, 50)
        } catch (_) {}

        // keep poster as the static PNG
        try { v.poster = this.bgUrlValue || "" } catch (_) {}

        // Sound behavior:
        // - Default muted.
        // - If the user explicitly enabled sound (via CTA), do NOT re-mute here.
        //   Turbo Stream updates can call this method again; we must respect the current state.
        if (this._soundEnabled) {
            try { v.muted = false } catch (_) {}
            try { v.removeAttribute("muted") } catch (_) {}
            try { v.volume = 1 } catch (_) {}
        } else {
            try { v.muted = true } catch (_) {}
            try { v.setAttribute("muted", "") } catch (_) {}
        }

        // already showing the same url
        if (v.dataset.currentSrc === url && !v.classList.contains("d-none")) {
            this._playBgVideoSafe(v)
            return
        }

        v.dataset.currentSrc = url
        v.src = url
        v.classList.remove("d-none")

        try { v.load() } catch (_) {}

        // If sound is enabled, force a pause→play cycle to apply audio state immediately.
        if (this._soundEnabled) {
            try { v.pause?.() } catch (_) {}
            this._playBgVideoSafe(v)
        } else {
            this._playBgVideoSafe(v)
        }
    }

    _playBgVideoSafe(v) {
        const p = v.play?.()
        if (p && typeof p.catch === "function") {
            p.catch(() => {
                // autoplay might be blocked in rare cases; leaving visible is fine
            })
        }
    }

    _deferUnmuteOnNextGesture() {
        if (!this.hasBgVideoTarget) return
        if (this._pendingUnmute) return
        this._pendingUnmute = true

        this._unmuteOnGesture = () => {
            this._pendingUnmute = false
            try {
                const v = this.bgVideoTarget
                if (!v) return
                try { v.muted = false } catch (_) {}
                try { v.removeAttribute("muted") } catch (_) {}
                this._playBgVideoSafe(v)
            } catch (_) {}
            window.removeEventListener("pointerdown", this._unmuteOnGesture, true)
            this._unmuteOnGesture = null
        }

        // capture = true ensures we catch it early
        window.addEventListener("pointerdown", this._unmuteOnGesture, true)
    }

    _stopAndHideBgVideo() {
        if (!this.hasBgVideoTarget) return
        const v = this.bgVideoTarget
        if (!v) return

        try { v.pause?.() } catch (_) {}
        v.classList.add("d-none")

        // Cancel any stale callbacks from _showBgVideo that could re-hide the PNG background after turning off.
        this._bgVideoSeq = (this._bgVideoSeq || 0) + 1
        if (this._bgVideoHideTimeoutId) {
            try { clearTimeout(this._bgVideoHideTimeoutId) } catch (_) {}
            this._bgVideoHideTimeoutId = null
        }

        // Restore the Konva PNG background.
        this._setKonvaPngBackgroundVisible(true)

        if (this._unmuteOnGesture) {
            window.removeEventListener("pointerdown", this._unmuteOnGesture, true)
            this._unmuteOnGesture = null
        }
        this._pendingUnmute = false

        try {
            v.removeAttribute("src")
            v.load?.()
            v.currentTime = 0
        } catch (_) {}

        delete v.dataset.currentSrc
    }

    _ensureBgImageRef() {
        // In fresh init, _loadBackground sets this.bgImage.
        // In JSON rehydration, the background Konva.Image may exist but bgImage is nil.
        if (this.bgImage && this.bgImage.getStage?.()) return this.bgImage

        // Prefer the first Konva.Image on bgLayer if present.
        try {
            const imgs = this.bgLayer?.find?.('Image') || []
            if (imgs.length > 0) {
                this.bgImage = imgs[0]
                return this.bgImage
            }
        } catch (_) {}

        // Fallback: search the stage for a non-listening full-canvas Image.
        try {
            const candidates = this.stage?.find?.('Image') || []
            const hit = candidates.find(n => {
                try {
                    const listening = (typeof n.listening === 'function') ? n.listening() : n.getAttr?.('listening')
                    if (listening !== false) return false
                    // Many of our backgrounds are 600x800 at 0,0 in stage space.
                    const w = (typeof n.width === 'function') ? n.width() : n.getAttr?.('width')
                    const h = (typeof n.height === 'function') ? n.height() : n.getAttr?.('height')
                    const x = (typeof n.x === 'function') ? n.x() : n.getAttr?.('x')
                    const y = (typeof n.y === 'function') ? n.y() : n.getAttr?.('y')
                    return (x === 0 && y === 0 && w === BASE_W && h === BASE_H)
                } catch (_) {
                    return false
                }
            })
            if (hit) {
                this.bgImage = hit
                return this.bgImage
            }
        } catch (_) {}

        return null
    }

    _setKonvaPngBackgroundVisible(visible) {
        // Background image is a Konva.Image stored as this.bgImage and lives on this.bgLayer
        const bg = this._ensureBgImageRef()
        if (!bg) return

        // If we're already in the desired state, avoid extra draws
        const desired = visible ? 1 : 0
        if (typeof bg.opacity === "function" && bg.opacity() === desired) return

        // Use opacity instead of visible(false) so it keeps sizing/positioning intact
        bg.opacity(desired)

        // Redraw only the background layer (cheap)
        if (typeof this.bgLayer?.batchDraw === "function") {
            this.bgLayer.batchDraw()
        } else if (this.stage && typeof this.stage.batchDraw === "function") {
            this.stage.batchDraw()
        }
    }

    async _loadInitialDesign(url) {
        let hydrated = false;

        const tryLoad = async (payloadOrString, label) => {
            if (!payloadOrString) return false;
            try {
                const payload = (typeof payloadOrString === "string")
                    ? JSON.parse(payloadOrString)
                    : payloadOrString;
                // remember saved slot rect (if any)
                this._savedPhotoSlotRect = payload.photoSlot || payload.base?.photoSlot || null;

                await this._loadJson(payload);
                this._wireAllTextNodes();
                // Ensure bgImage reference exists in rehydrated designs (needed for video overlay)
                this._ensureBgImageRef()
                // Re-evaluate video/background state now that stage nodes exist
                queueMicrotask(() => this._syncVideoBackgroundFromCta())
                // seed side panel once so fonts/colors reflect current selection
                this._selectFirstRoleText();
                this._didAutoPlace = false;          // never auto-layout after JSON
                this._updateEmptyStateVisibility();
                hydrated = true;
                return true;
            } catch (e) {
                console.warn(`${label} rehydrate failed`, e);
                return false;
            }
        };

        // 1) Inline data attribute
        if (this.hasInitialJsonValue && this.initialJsonValue) {
            if (await tryLoad(this.initialJsonValue, "initialJsonValue")) return;
        }

        // 2) Hidden field (kept current by _syncJson)
        if (this.hasJsonFieldTarget && this.jsonFieldTarget.value) {
            if (await tryLoad(this.jsonFieldTarget.value, "hidden jsonField")) return;
        }

        // 2.5) Party editor mode: hydrate from the party itself
        if (this.contextValue === "party" && this.hasPartyTokenValue) {
            try {
                const resp = await fetch(`/party/${encodeURIComponent(this.partyTokenValue)}/edit/design.json`, { credentials: 'same-origin' });
                if (resp.ok) {
                    const { editor_json } = await resp.json();
                    if (await tryLoad(editor_json, "party editor_json")) return;
                }
            } catch (e) {
                console.warn("party editor fetch failed", e);
            }
        }

        // 3) Server draft (fallback)
        if (this.hasDraftIdValue && this.draftIdValue) {
            try {
                const resp = await fetch(`/party_drafts/${this.draftIdValue}`, { credentials: 'same-origin' });
                if (resp.ok) {
                    const { editor_json } = await resp.json();
                    if (await tryLoad(editor_json, "server draft")) return;
                }
            } catch (e) {
                console.warn("server draft fetch failed", e);
            }
        }

        // 4) Fresh init (only if nothing rehydrated)
        if (!hydrated) {
            await this._loadBackground(url);
            await this._waitForBg();
            await this._analyzeBackground(this.bgImage.image());

            // NEW: if we have editor_hints, use them instead of heuristic auto-layout
            const hasHints =
                this.hasEditorHintsValue &&
                this.editorHintsValue &&
                Array.isArray(this.editorHintsValue.text_blocks) &&
                this.editorHintsValue.text_blocks.length > 0;

            if (hasHints) {
                await this._initFromHints(this.editorHintsValue);
                // Hints already define positions, so don't auto-place
                this._didAutoPlace = false;
            } else {
                // Old behaviour: sample text + heuristic layout
                this._ensureSampleText();
                await this._applyHintsOrAutoLayout();
            }

            this.content.batchDraw();
            this._updateEmptyStateVisibility();

            // remember freshly computed slot rectangle for future saves
            const slotNow = this._holeRectStage(16);
            if (slotNow) this._savedPhotoSlotRect = slotNow;
            this._ensurePhotoSlot();

            // NEW: once all fonts are ready, force a re-measure/redraw of text
            if (document.fonts?.ready) {
                document.fonts.ready.then(() => {
                    // Guard against controller being disconnected meanwhile
                    if (this.stage && !this.stage.isDestroyed?.()) {
                        this._refreshTextFontRendering();
                    }
                });
            }
        }
    }

    _loadFromHiddenField() {
        if (!this.hasJsonFieldTarget) return;
        const v = this.jsonFieldTarget.value;
        if (!v) return;
        try { this._loadJson(JSON.parse(v)); this._wireAllTextNodes(); } catch {}
    }

    _waitForBg() {
        return new Promise(resolve => {
            const check = () => (this.bgImage && this.bgImage.image()) ? resolve() : setTimeout(check, 16);
            check();
        });
    }

    _applyToActive(fn) {
        const n = this._active
        const text = this._resolveTextNode(n)
        if (!text) return
        fn(text)
        this.content.batchDraw()
        // Re-broadcast so side panel reflects the new color/size/etc
        this._emitInspect(text)
    }

    // Find a Text even if the active node is a Group
    _resolveTextNode(n) {
        if (!n) return null
        const klass = n.getClassName?.()
        if (klass === "Text") return n

        // (Optional, but explicit)
        const nm = n.name?.();
        if (nm === "text") return n;

        // If it’s a Group or something else, try to find the first Text inside
        if (typeof n.find === "function") {
            const t = n.find("Text")[0]
            if (t) return t
        }
        return null
    }

    _schedule(fn) {
        clearTimeout(this._t)
        this._t = setTimeout(fn, 50)
    }

    // Create a draggable/transformable sticker image, centered on the canvas
    async _addSticker(src, name = "sticker") {
        try {
            const img = await loadImage(src);
            const iw = img.naturalWidth || img.width;
            const ih = img.naturalHeight || img.height;

            // scale down large assets to a friendly starting size
            const MAX = 180;
            const scale = Math.min(1, MAX / Math.max(iw, ih));
            const w = Math.round(iw * scale);
            const h = Math.round(ih * scale);

            const node = new Konva.Image({
                image: img,
                width: w,
                height: h,
                x: Math.round((BASE_W - w) / 2),
                y: Math.round((BASE_H - h) / 2),
                draggable: true,
                listening: true,
                name: "sticker",
                id: `sticker-${Date.now()}`
            });

            this.stickersLayer.add(node);
            this.stickersLayer.batchDraw();

            // Use the new centralized function for all listeners
            this._attachInteractionListeners(node);

            this._select(node);
        } catch (e) {
            console.warn("Sticker load failed:", e);
        }
    }

    _handleContinue() {
        // One export/save at a time, even with repeated taps on a slow connection.
        if (this._continuePromise) return this._continuePromise;
        if (this._uploadingPhoto) {
            Swal.fire({ icon: "info", title: "Photo still uploading", text: "Please wait for your photo to appear, then save your invitation.", showConfirmButton: true });
            return Promise.resolve();
        }
        if (this.contextValue !== 'party') window.echoageSetupDiagnostics?.report?.('editor_continue_attempt');
        this._continuePromise = (async () => {
            await this._initPromise;
            if (!this._initialized || this._editorDisconnected) {
                throw new Error("Your invitation is still loading. Please wait and try again.");
            }
            this._finishInlineEdit?.();
            this._savingFinal = true;
            this._setSaveBusy(true);
            this._autosaveJsonDebounced?.cancel();
            // Let already-dispatched writes finish before the authoritative save.
            // A failed autosave is recoverable: the final request contains the full design.
            await this._saveQueue;
            if (this._editorDisconnected) return;
            const snapshot = _normalizeForSave(this._dumpJson());
            await this._saveAndContinue(snapshot);
        })().catch(error => {
            if (this.contextValue !== 'party') window.echoageSetupDiagnostics?.report?.('editor_continue_failed', 'save_failed');
            console.warn("Invitation save failed:", error);
            Swal.fire({ icon: "error", title: "Changes not saved", text: error.message || "Please stay on this page and try again." });
        }).finally(() => {
            this._savingFinal = false;
            this._continuePromise = null;
            this._setSaveBusy(false);
        });
        return this._continuePromise;
    }

    _setSaveBusy(busy) {
        const editor = this.element?.closest("#editor");
        if (!editor) return;
        editor.inert = busy;
        editor.setAttribute("aria-busy", String(busy));
    }

    async _saveAndContinue(snapshot) {
        // --- Toast helper (one toast, updated as we go) ---
        const Toast = {
            open(title = "Working…", html = "") {
                Swal.fire({
                    toast: true,
                    position: "bottom-end",
                    icon: "info",
                    title,
                    html,
                    timer: 0,
                    showConfirmButton: false,
                    customClass: { popup: "swal2-ea-popup", title: "swal2-ea-title" },
                    didOpen: () => Swal.showLoading(),
                });
            },
            step(title, html = "") { Swal.update({ icon: "info", title, html }); },
            progress(pct) {
                const bar = `
        <div style="width:180px;height:6px;border-radius:999px;background:#E5E7EB;overflow:hidden;margin-top:6px">
          <div style="width:${pct}%;height:100%;background:#0EA5E9;transition:width .15s"></div>
        </div>`;
                Swal.update({ html: bar });
            },
            success(title = "All set!") {
                Swal.update({ icon: "success", title, html: "" });
                setTimeout(() => Swal.close(), 900);
            },
            error(title = "Something went wrong", text = "") {
                Swal.hideLoading();
                Swal.update({ icon: "error", title, text, html: "", showConfirmButton: true, confirmButtonText: "OK" });
            }
        };

        // --- Ensure we have a draft (create on demand) ---
        if (this.contextValue === "draft") await this._createOrReuseDraft(snapshot);
        if (this.contextValue === "party" && !this.partyTokenValue) throw new Error("Party could not be identified. Please reopen it from your dashboard.");

        // --- RENDER ---
        Toast.open("Rendering invitation…");

        // Hide frames/transformer for clean export
        const prevTextStates = [];
        const textNodesForExport = this.content?.find?.('Text') || [];

        textNodesForExport.forEach((n) => {
            if (!n) return;
            prevTextStates.push([n, n.opacity(), n._frame?.visible()]);
            // ensure text itself is fully visible in the export
            n.opacity(1);
            // hide dashed selection frame if present
            if (n._frame) n._frame.visible(false);
        });

        // Hide transformer box if present
        let prevTransformerVisible = null;
        if (this.transformer && typeof this.transformer.visible === 'function') {
            prevTransformerVisible = this.transformer.visible();
            this.transformer.visible(false);
        }

        // Hide the HTML mini-toolbar, if any
        this._hideMiniToolbar?.();

        this.content.batchDraw();
        this.uiLayer?.batchDraw();
        this._hideSlotPrompt();

        // --- Export notes (IMPORTANT) ---
        // When animation is enabled we hide the Konva PNG background (opacity 0) so the HTML <video> can show beneath.
        // But our exports are pure Konva canvas snapshots, so we MUST force the PNG background visible for the main
        // preview export, then hide it only for the overlay export, and finally restore the previous state.

        const bgForExport = this._ensureBgImageRef?.();
        const prevBgOpacityForExport = (bgForExport && typeof bgForExport.opacity === "function") ? bgForExport.opacity() : null;

        // 1) MAIN PREVIEW EXPORT (always includes the original invitation background)
        try {
            if (bgForExport && typeof bgForExport.opacity === "function") {
                bgForExport.opacity(1);
                this.bgLayer?.batchDraw?.();
            }
        } catch (_) {}

        // Snapshot → Blob (FULL invitation: background + user edits)
        const blob = await new Promise(res => {
            this.stage.toCanvas({ pixelRatio: 2 }).toBlob(b => res(b), 'image/png', 1.0);
        });

        // 2) OVERLAY EXPORT (transparent background, only user edits)
        let overlayBlob = null;
        try {
            if (bgForExport && typeof bgForExport.opacity === "function") {
                bgForExport.opacity(0);
                this.bgLayer?.batchDraw?.();
            }

            // Guest designs are static; their upload endpoint intentionally
            // rejects animation overlays. Keep the full preview only.
            if (!this.anonymousValue) {
                overlayBlob = await new Promise(res => {
                    this.stage.toCanvas({ pixelRatio: 2 }).toBlob(b => res(b), 'image/png', 1.0);
                });
            }
        } finally {
            // 3) Restore whatever state the editor was in before exporting (could be 0 when video is showing)
            if (prevBgOpacityForExport !== null) {
                try {
                    bgForExport.opacity(prevBgOpacityForExport);
                    this.bgLayer?.batchDraw?.();
                } catch (_) {}
            }
        }

        // Restore UI
        prevTextStates.forEach(([n, op, vis]) => {
            n.opacity(op);
            if (n._frame) n._frame.visible(vis !== false);
        });

        if (this.transformer && prevTransformerVisible !== null) {
            this.transformer.visible(prevTransformerVisible);
        }

        this.content.batchDraw();
        this.uiLayer?.batchDraw();

        // --- UPLOAD (with progress) ---
        Toast.step('Preparing upload…');
        let uploadURL;
        try {
            const ticket = await jsonFetch("/cf_images/direct_upload", {
                method: "POST",
                body: JSON.stringify({ draft_ref: this.draftId, kind: "preview" })
            });
            uploadURL = ticket?.result?.uploadURL || ticket?.uploadURL;
            this._previewUploadTicket = ticket?.result || ticket;
        } catch (e) {
            if (e.authenticationRequired) this._showSetupSignIn();
            else Toast.error("Upload error", "Your edits are still here. Keep this page open and try again.");
            return;
        }
        if (!uploadURL) { Toast.error("Upload error", "No upload URL from server."); return; }

        Toast.step("Uploading invitation…");
        const fd = new FormData();
        fd.append("file", blob, "preview.png");


        // --- START: NEW RETRY LOGIC ---

        /**
         * A helper function that attempts to upload a file using XHR and retries on failure.
         * @param {string} url - The destination URL for the upload.
         * @param {FormData} formData - The data to upload.
         * @param {number} maxRetries - The total number of attempts to make.
         * @returns {Promise<object>} - The parsed JSON response on success.
         */
        async function uploadWithRetries(url, formData, maxRetries = 3) {
            for (let attempt = 1; attempt <= maxRetries; attempt++) {
                try {
                    // Wrap the XHR request in a new Promise for each attempt
                    const upJson = await new Promise((resolve, reject) => {
                        const xhr = new XMLHttpRequest();
                        xhr.open("POST", url);
                        xhr.upload.onprogress = (e) => {
                            if (e.lengthComputable) {
                                const pct = Math.round((e.loaded / e.total) * 100);
                                Toast.progress(pct);
                            }
                        };
                        xhr.onload = () => {
                            try { resolve(JSON.parse(xhr.responseText)); }
                            catch (err) { reject(new Error("Failed to parse upload response.")); }
                        };
                        xhr.onerror = () => reject(new Error("Network error during upload."));
                        xhr.send(formData);
                    });

                    // Check for a valid Cloudflare image ID in the response
                    const imageId = upJson?.result?.id;
                    if (imageId) {
                        return upJson; // SUCCESS: Return the valid response and exit the loop.
                    }

                    // If no imageId, it's a "soft" failure. Log it and let the loop retry.
                    console.warn(`Upload attempt ${attempt} succeeded, but no image ID was found.`);

                } catch (error) {
                    // This is a "hard" failure (e.g., network error). Log it and let the loop retry.
                    console.error(`Upload attempt ${attempt} failed:`, error);
                }

                // If we've reached here, the attempt failed. If it's not the last attempt, wait a bit.
                if (attempt < maxRetries) {
                    await new Promise(res => setTimeout(res, 1000)); // Wait 1 second before retrying
                }
            }

            // If the loop completes without a successful return, all retries have failed.
            throw new Error(`Upload failed after ${maxRetries} attempts.`);
        }


        // Use XHR to get real upload progress
        let upJson;
        try {
            upJson = await uploadWithRetries(uploadURL, fd, 3);
        } catch (e) {
            console.error(e); // Log the final error
            Toast.error("Upload failed", "Please check your connection and try again.");
            return;
        }

        const imageId = upJson?.result?.id;
        if (!imageId) { Toast.error("Upload failed", "No image ID from Cloudflare."); return; }
        if (this._previewUploadTicket?.private) {
            try {
                await jsonFetch(`/cf_images/${encodeURIComponent(imageId)}/complete`, { method: "POST" });
            } catch (e) {
                Toast.error("Save failed", "Couldn’t secure the temporary invitation upload.");
                return;
            }
        }

        // --- SAVE ---
        Toast.step("Saving…");
        const HASH = window.CF_DELIVERY_HASH || document.querySelector('meta[name="cf-delivery-hash"]')?.content || "";
        const previewUrl = this._previewUploadTicket?.deliveryURL || `https://imagedelivery.net/${HASH}/${imageId}/original`;

        // Upload overlay PNG (transparent) to Cloudflare Images as well
        let overlayUrl = null;
        if (overlayBlob) {
            try {
                const overlayTicket = await jsonFetch("/cf_images/direct_upload", {
                    method: "POST",
                    body: JSON.stringify({ draft_ref: this.draftId, kind: "overlay" })
                });
                const overlayUploadURL = overlayTicket?.result?.uploadURL || overlayTicket?.uploadURL;
                if (overlayUploadURL) {
                    const fd2 = new FormData();
                    fd2.append("file", overlayBlob, "overlay.png");
                    const overlayJson = await uploadWithRetries(overlayUploadURL, fd2, 3);
                    const overlayId = overlayJson?.result?.id;
                    if (overlayId) {
                        if (overlayTicket?.result?.private) {
                            await jsonFetch(`/cf_images/${encodeURIComponent(overlayId)}/complete`, { method: "POST" });
                        }
                        overlayUrl = overlayTicket?.result?.deliveryURL || `https://imagedelivery.net/${HASH}/${overlayId}/original`;
                    }
                }
            } catch (e) {
                console.warn("Overlay upload failed; continuing without overlay", e);
            }
        }

        try {
            if (this.contextValue === "party" && this.hasPartyTokenValue) {
                // Update the party directly
                await jsonFetch(`/party/${encodeURIComponent(this.partyTokenValue)}/edit/design`, {
                    method: "PATCH",
                    body: JSON.stringify({
                        editor_json: snapshot,
                        preview_url: previewUrl,
                        animation_overlay_url: overlayUrl
                    })
                });
            } else {
                // Draft mode (existing)
                await jsonFetch(`/party_drafts/${this.draftId}`, {
                    method: "PATCH",
                    body: JSON.stringify({
                        editor_json: snapshot,
                        preview_url: previewUrl,
                        status: "details",
                        fields_json: {
                            party: {
                                animation_overlay_url: overlayUrl
                            }
                        }
                    })
                });
            }
        } catch (e) {
            console.warn(e);
            if (e.authenticationRequired) this._showSetupSignIn();
            else Toast.error("Save failed", "Your edits are still here. Keep this page open and try again.");
            return;
        }
        // --- DONE ---

        // If party edit
        if (this.contextValue === "party" && this.hasPartyTokenValue) {
            this._navigatingAway = true;
            Toast.success("Saved!");
            window.location.assign(`/dashboard/manage/${encodeURIComponent(this.partyTokenValue)}`);
            return;
        }
        // If Draft
        this._navigatingAway = true;
        window.echoageSetupDiagnostics?.report?.('editor_continue_saved');
        Toast.success("Invitation ready!");
        const sgid = this._draftSgid || (this.hasDraftSgidValue ? this.draftSgidValue : null);
        const nextUrl = this.anonymousValue
            ? `/party/details?draft_ref=${encodeURIComponent(this.draftId)}`
            : (sgid
                ? `/party/details?draft_sgid=${encodeURIComponent(sgid)}`
                : `/party/details?draft_id=${encodeURIComponent(this.draftId)}`);
        window.location.assign(nextUrl);
    }

    // -------- UI actions --------
    choosePhoto() {
        if (this.hasPhotoEnabledValue && !this.photoEnabledValue) return
        this.photoInputTarget?.click()
    }

    openPreview() {
        // Simple data URL preview for now
        const dataURL = this.stage.toDataURL({ pixelRatio: 2 })
        const w = window.open("", "_blank")
        if (w) {
            w.document.write(`<img src="${dataURL}" style="max-width:100%"/>`)
        }
    }

    addDefaultTexts() {
        // Reset primary references so _ensureSampleText actually rebuilds them
        this.titleNode = null;
        this.whenNode  = null;
        this.timeNode  = null;
        this.whereNode = null;

        this._ensureSampleText();
        this.content.batchDraw();
        this._updateEmptyStateVisibility();
        this._syncJson();
    }

    _loadBackground(url) {
        // de-dup same URL loads
        if (this._bgLoading && this._bgUrl === url) return this._bgLoading

        this._bgUrl = url
        this._bgLoading = new Promise((resolve, reject) => {
            const img = new Image()
            img.crossOrigin = "anonymous"
            img.onload = () => {
                if (this.bgImage) this.bgImage.destroy()
                this.bgImage = new Konva.Image({
                    image: img,
                    x: 0, y: 0, width: BASE_W, height: BASE_H,
                    listening: false,
                    perfectDrawEnabled: false
                })
                this.bgLayer.destroyChildren()
                this.bgLayer.add(this.bgImage)
                this.bgLayer.batchDraw()
                resolve(this.bgImage)            // ← resolve after it’s set

            }
            img.onerror = (e) => reject(e)
            img.src = url
        })

        return this._bgLoading
    }

    photoInputTargetConnected(el) {
        el.addEventListener("change", async (ev) => {
            const file = ev.target.files?.[0];
            if (!file) return;
            if (file.size > 10 * 1024 * 1024) {
                Swal.fire({ icon: "error", title: "Photo is too large", text: "Please choose an image smaller than 10 MB." });
                ev.target.value = "";
                return;
            }
            if (this._uploadingPhoto || this._savingFinal || this._navigatingAway || this._editorDisconnected) return;
            this._suppressAutosaveUntilInteraction = false;
            this._uploadingPhoto = true;
            try {
                // show toast feedback right away
                Swal.fire({
                    toast: true,
                    title: "Uploading photo…",
                    icon: "info",
                    position: "bottom-end",
                    timer: 0,                // no auto-close, let us close manually
                    showConfirmButton: false,
                    customClass: {
                        popup: "swal2-ea-popup",
                        title: "swal2-ea-title"
                    },
                    didOpen: (toast) => {
                        toast.addEventListener("mouseenter", Swal.stopTimer)
                        toast.addEventListener("mouseleave", Swal.resumeTimer)
                    }
                });
                // Background ready
                await this._initPromise;
                if (this._editorDisconnected) return;
                await this._waitForBg();
                if (!this._bgSample) await this._analyzeBackground(this.bgImage.image());

                // Ensure slot (clipped rounded-rect)
                const slot = this._ensurePhotoSlot();

                // Decode locally first. A bad image must not replace the saved
                // photo's ID or start a provider upload, and always free the URL.
                const objUrl = URL.createObjectURL(file);
                let img;
                try { img = await loadImage(objUrl); }
                finally { URL.revokeObjectURL(objUrl); }
                if (this._editorDisconnected) return;

                // Existing parties upload as the signed-in account, exactly as
                // their preview exports do. Only setup drafts need a draft ref
                // (including the session-owned token for private guest uploads).
                if (this.contextValue === "draft") {
                    await this._createOrReuseDraft();
                    if (!this.draftId) throw new Error("Could not create a temporary draft");
                }
                if (this._editorDisconnected) return;
                const draftRef = this.contextValue === "draft" ? this.draftId : undefined;
                const uploaded = await directUploadToCloudflare(file, { draftRef, kind: "photo" });
                if (this._editorDisconnected) return;
                Swal.close();
                this._photoImageId = uploaded.imageId;
                this._photoUrl = uploaded.url || null;

                // Reset any existing nodes
                if (this.photoCarrier) { this.photoCarrier.destroy(); this.photoCarrier = null; }
                if (this.photoNode)    { this.photoNode.destroy();    this.photoNode = null;    }

                // Carrier is the draggable/transformable group (never the raw Image)
                this.photoCarrier = new Konva.Group({ draggable: true, listening: true, name: "photoCarrier" });
                slot.add(this.photoCarrier);

                // Image node frozen at (0,0) inside carrier
                this.photoNode = new Konva.Image({ image: img, draggable: false, listening: true, name: "photoNode" });
                this.photoCarrier.add(this.photoNode);

                // Cover-fit inside slot + 5% bleed (stable ordering)
                const fit = this._fitCover(
                    img.naturalWidth || img.width,
                    img.naturalHeight || img.height,
                    slot.width(),
                    slot.height()
                );
                this.photoCarrier.scale({ x: 1, y: 1 });
                this.photoNode.size({ width: fit.w, height: fit.h });
                this.photoCarrier.size({ width: fit.w, height: fit.h });
                this.photoCarrier.scale({ x: 1, y: 1 });
                this.photoCarrier.position({ x: fit.x, y: fit.y });
                this.photoCarrier.rotation(0);

                // Wire selection and autosave
                this._attachPhotoListeners();

                this._hideSlotPrompt();
                this._select(this.photoCarrier);
                document.dispatchEvent(new CustomEvent("ie:inspect", { detail: { kind: "Photo" } }));
                this.photoLayer.batchDraw();
                this._syncJson();
            } catch(e) {
                console.warn(e);
                if (!this._editorDisconnected) {
                    Swal.close();
                    Swal.fire({
                        icon: "error", title: "Photo not uploaded",
                        text: e.message === "HTTP 401"
                            ? "Your session has expired. Please sign in again, reopen the invitation and choose your photo."
                            : "We couldn't add your photo. Please try choosing it again. Check your connection, or try a JPG or PNG smaller than 10 MB.",
                        showConfirmButton: true, confirmButtonText: "OK"
                    });
                }
            } finally {
                ev.target.value = ""; // Allow choosing the same file again after a failure.
                this._uploadingPhoto = false;
            }
        });
    }

    // call this whenever selection changes
    _emitInspect(node) {
        // Resolve to a Text if the selection is a Group or Image, etc.
        const t = this._resolveTextNode?.(node);
        if (!t || !t.getStage?.() || t.getClassName?.() !== "Text") return;
        const state = {
            color: t.fill?.() || "#000000",
            fontFamily: t.fontFamily?.() || "Arial",
            fontWeight: t.fontStyle?.() || "normal",
            fontSize: parseInt(t.fontSize?.() || 24, 10),
            align: t.align?.() || "center",
            case:  t.getAttr?.("_case") || "uppercase",
            kind:  "Text"
        };
        document.dispatchEvent(new CustomEvent("ie:inspect", { detail: state }));
    }

    // Adds the button to create a text node
    _updateEmptyStateVisibility() {
        if (!this.hasEmptyStateTarget) return;
        const anyText = this.content?.find?.('Text')?.length > 0;
        this.emptyStateTarget.classList.toggle('d-none', !!anyText);
    }
    // -------- Private: text nodes --------
    _ensureSampleText() {
        // If this design came from AI hints, do NOT auto-create missing roles.
        // The absence of a role in text_blocks is intentional (e.g. no "time" field).
        if (this._bootstrappedFromHints) return;
        if (this.titleNode) return // already built
        this.titleNode = this._makeText(SAMPLE.title, {
            x: BASE_W * 0.08, y: BASE_H * 0.06, fontSize: 30, fontStyle: "bold", fill: "#1a2d70"
        }, "title")
        this.whenNode  = this._makeText(SAMPLE.when,  {
            x: BASE_W * 0.08, y: BASE_H * 0.14, fontSize: 20, fill: "#1a2d70"
        }, "when")
        // NEW: time node
        this.timeNode  = this._makeText(SAMPLE.time,  {
            x: BASE_W * 0.08, y: BASE_H * 0.18, fontSize: 20, fill: "#1a2d70"
        }, "time");
        this.whereNode = this._makeText(SAMPLE.where, {
            x: BASE_W * 0.08, y: BASE_H * 0.82, fontSize: 22, fill: "#1a2d70"
        }, "where")

        this.content.add(this.titleNode, this.whenNode, this.timeNode, this.whereNode)
        this.content.draw()

        // Focus first by default
        this._select(this.titleNode)
        this._didAutoPlace = true
        // Wire all text nodes and frames
        this._wireAllTextNodes()
        // Add the button for the new text node
        this._updateEmptyStateVisibility();
    }

    async _initFromHints(hints) {
        // mark that this design was created from AI hints
        this._bootstrappedFromHints = true;
        const blocks = Array.isArray(hints?.text_blocks) ? hints.text_blocks : [];
        if (!blocks.length) {
            // No hints → fall back to old behaviour
            this._ensureSampleText();
            return;
        }

        const baseH = (hints.base && typeof hints.base.h === "number") ? hints.base.h : BASE_H;

        const pickBlock = (role) => blocks.find(b => b && b.role === role);

        const makeNode = async (role, defaultText, defaults = {}) => {
            const b = pickBlock(role);
            if (!b) return null;

            const fontFamily = b.font_family || defaults.fontFamily || "Arial";
            try {
                await ensureFontLoaded(fontFamily);
            } catch (_) {
                // ignore font load failures
            }

            const textFromHints =
                typeof b.sample_text === "string" ? b.sample_text.trim() : "";
            const text = textFromHints.length > 0 ? textFromHints : defaultText;

            const node = new Konva.Text({
                text,
                x: defaults.x ?? BASE_W * 0.08,
                y: defaults.y ?? BASE_H * 0.10,
                width: defaults.width ?? BASE_W * 0.86,
                draggable: true,
                listening: true,
                align: b.align || defaults.align || "center",
                fontFamily,
                fill: b.color || defaults.fill || "#1a2d70",
                id: role,
                name: role
            });

            // Keep our comfortable default sizes
            if (typeof defaults.fontSize === "number") {
                node.fontSize(defaults.fontSize);
            }

            // Store a *normalized* vertical hint (0..1) if we have y + h
            if (typeof b.y === "number" && typeof b.h === "number" && baseH > 0) {
                const centerY = b.y + b.h / 2;
                let norm = centerY / baseH;
                if (norm < 0) norm = 0;
                if (norm > 1) norm = 1;
                node.setAttr("_hintY", norm);
            }

            this._attachSelectable(node);
            return node;
        };

        // Use same role defaults as _ensureSampleText
        this.titleNode = await makeNode("title", SAMPLE.title, {
            x: BASE_W * 0.08,
            y: BASE_H * 0.06,
            width: BASE_W * 0.86,
            fontSize: 30
        });

        this.whenNode = await makeNode("when", SAMPLE.when, {
            x: BASE_W * 0.08,
            y: BASE_H * 0.14,
            width: BASE_W * 0.86,
            fontSize: 20
        });

        this.timeNode = await makeNode("time", SAMPLE.time, {
            x: BASE_W * 0.08,
            y: BASE_H * 0.18,
            width: BASE_W * 0.86,
            fontSize: 20
        });

        this.whereNode = await makeNode("where", SAMPLE.where, {
            x: BASE_W * 0.08,
            y: BASE_H * 0.82,
            width: BASE_W * 0.86,
            fontSize: 22
        });

        [this.titleNode, this.whenNode, this.timeNode, this.whereNode]
            .filter(Boolean)
            .forEach(node => this.content.add(node));

        this.content.draw();

        // Wire text nodes (frames, listeners etc.)
        this._wireAllTextNodes?.();

        // IMPORTANT: now let our normal auto-layout place them,
        // but it will look at each node’s _hintY for vertical preference.
        await this._applyHintsOrAutoLayout();

        // Prefer title node as initial selection so side panel is in sync
        if (this.titleNode && this.titleNode.getStage && this.titleNode.getStage()) {
            this.selected = this.titleNode;
        }
        // This will call _select(..) internally and emit ie:inspect
        this._selectFirstRoleText();

        this._updateEmptyStateVisibility();

        if (document.fonts?.ready) {
            document.fonts.ready.then(() => {
                if (!this.stage || this.stage.isDestroyed?.()) return;
                this._refreshTextFontRendering();
            });
        }
    }

    _resolveHintOverlaps() {
        const nodes = [this.titleNode, this.whenNode, this.timeNode, this.whereNode]
            .filter(n => n && n.getStage());

        if (!nodes.length) return;

        // Sort from top to bottom
        nodes.sort((a, b) => a.y() - b.y());

        const minGap = 8; // px vertical gap between blocks

        for (let i = 1; i < nodes.length; i++) {
            const prev = nodes[i - 1];
            const cur  = nodes[i];

            // Measure rects relative to the content layer, same as frames
            const prevRect = prev.getClientRect({ skipShadow: true, skipStroke: true, relativeTo: this.content });
            const curRect  = cur.getClientRect({  skipShadow: true, skipStroke: true, relativeTo: this.content });

            const desiredTop = prevRect.y + prevRect.height + minGap;
            const delta = desiredTop - curRect.y;

            if (delta > 0) {
                // Push current block down just enough so it no longer overlaps.
                cur.y(cur.y() + delta);
                this._updateFrame?.(cur);
            }
        }

        this.content.batchDraw();
    }

    _nudgeHintsOffArtwork() {

        const nodes = [this.titleNode, this.whenNode, this.timeNode, this.whereNode]
            .filter(n => n && n.getStage());

        if (!nodes.length || !this._bgSample) return;

        const maxShift = 80;   // max pixels we’re willing to move from hinted Y
        const step     = 4;    // shift step
        const placedRects = [];

        const rectFor = (node) => {
            const r = node.getClientRect({
                skipShadow: true,
                skipStroke: true,
                relativeTo: this.content
            });
            return { x: r.x, y: r.y, w: r.width, h: r.height };
        };

        for (const node of nodes) {
            let rect = rectFor(node);

            // If already safe and not colliding previously nudged ones, keep as is
            if (!this._intersectsForbidden(rect) && !this._collidesExisting?.(rect, placedRects)) {
                placedRects.push(rect);
                continue;
            }

            const originalY = node.y();
            let bestY = originalY;
            let found = false;

            // Try nudging down then up within a limited band, staying near the hint
            const tryDirections = [1, -1];
            for (const dir of tryDirections) {
                for (let delta = step; delta <= maxShift; delta += step) {
                    const candidateY = originalY + dir * delta;
                    if (candidateY < 0 || candidateY + rect.h > BASE_H) continue;

                    const candidateRect = { ...rect, y: rect.y + dir * delta };
                    if (this._intersectsForbidden(candidateRect)) continue;
                    if (this._collidesExisting?.(candidateRect, placedRects)) continue;

                    bestY = candidateY;
                    rect  = candidateRect;
                    found = true;
                    break;
                }
                if (found) break;
            }

            // If nudging didn’t find anything, fall back to full placement search
            if (!found) {
                const boxW = rect.w;
                const boxH = rect.h;
                const centerYNorm = (rect.y + rect.h / 2) / BASE_H;

                const p = this._findPlacement({
                    boxW,
                    boxH,
                    blocks: placedRects,
                    preferY: centerYNorm,
                    preferWeight: 1.5
                });

                node.position({ x: p.x, y: p.y });
                rect = { x: p.x, y: p.y, w: boxW, h: boxH };
            } else {
                node.y(bestY);
            }

            this._updateFrame?.(node);
            placedRects.push(rect);
        }

        this.content.batchDraw();
    }

    _makeText(text, opts, role) {
        const node = new Konva.Text({
            text,
            fontFamily: "Arial",
            fontSize: 24,
            width: BASE_W * 0.86,
            draggable: true,
            listening: true,
            align: "center",
            id: role,
            name: role,
            ...opts
        })
        this._attachSelectable(node)
        return node
    }

    _attachSelectable(node) {
        node.off("click tap");
        node.on("click tap", () => this._select(node));
    }
    _select(node) {
        if (!node) return;

        if (node === this.photoNode) {
            node = this.photoCarrier;
        }

        const parent = node.getParent();
        if (!parent) return;

        if (!(this.transformer instanceof Konva.Transformer)) {
            this.transformer = new Konva.Transformer({
                rotateEnabled: true,
                enabledAnchors: ['middle-left', 'middle-right', 'top-left', 'top-right', 'bottom-left', 'bottom-right']
            });
        }

        const isPhoto = node === this.photoCarrier;
        const isSticker = node?.name?.() === 'sticker';
        const isText = node?.getClassName?.() === 'Text';
        if (isText) {
            this.transformer.enabledAnchors(['middle-left','middle-right']);
        } else {
            this.transformer.enabledAnchors(['middle-left','middle-right','top-left','top-right','bottom-left','bottom-right']);
        }

        // Always attach transformer to the SAME layer as the selected node.
        // const hostForTransformer = node.getLayer();
        // if (this.transformer.getParent() !== hostForTransformer) {
        //     this.transformer.remove();
        //     hostForTransformer.add(this.transformer);
        // }

        if (typeof this.transformer.nodes === 'function') {
            this.transformer.nodes([node]);
        } else if (typeof this.transformer.node === 'function') {
            this.transformer.node(node);
        }
        this.transformer.moveToTop();
        this.uiLayer.batchDraw();

        this.selected = node;
        if (node === this.photoCarrier || node === this.photoNode) {
            document.dispatchEvent(new CustomEvent("ie:inspect", {detail: {kind: "Photo"}}))
        } else if (isSticker) {
            document.dispatchEvent(new CustomEvent("ie:inspect", { detail: { kind: "Sticker" } }));
        } else {
            this._emitInspect(this.selected) // your existing text payload
        }
        this._reflectSelectionToPanel();

        [this.titleNode, this.whenNode, this.timeNode, this.whereNode]
            .filter(n => n && n.getStage())
            .forEach(n => {
                if (!n._frame) this._ensureFrameFor(n);
                this._updateFrame(n);
                this._setFrameActive(n, n === node);
            });
        this._showMiniToolbarFor(node);
    }

    _handleStageClick(e) {
        // Clicked empty area -> clear selection
        if (e.target === this.stage) {
            // === ADD THIS LOGIC ===
            // If an object was selected, move it back to its original layer before deselecting.
            if (this.selected) {
                // Check if it's a sticker. You can add more checks for other object types.
                if (this.selected.name() === 'sticker') {
                    this.selected.moveTo(this.stickersLayer);
                }
                // Example for other types if they also get moved:
                // if (this.selected.name() === 'user_photo') {
                //     this.selected.moveTo(this.photoLayer);
                // }
            }
            // === END OF ADDED LOGIC ===

            try {
                this.transformer.nodes([]);
            } catch (_) {}

            // Redraw the layers that were potentially modified
            this.stickersLayer.batchDraw();
            this.uiLayer.batchDraw();

            this.selected = null;
            this._hideMiniToolbar();
            this._updateEmptyStateVisibility();
        }
        // Note: If you have logic to handle clicking ON a shape, that logic
        // should also move the previously selected shape back to its layer.
    }
    async _inlineEdit(node) {
      if (!node) return;
      this._finishInlineEdit?.();

      // For plain duplicated text nodes (name === 'text'), edit them directly.
      // For canonical nodes (title/when/where/time or groups), resolve via _resolveTextNode.
      const isPlainText = typeof node.name === "function" && node.name() === "text";
      const target = isPlainText ? node : (this._resolveTextNode?.(node) || node);

      // hide canvas text + frame
      const prevOpacity = target.opacity();
      const prevFrameVisible = target._frame?.visible();
      target.opacity(0);
      if (target._frame) target._frame.visible(false);
      this.content.batchDraw();

      const host = this.stageHostTarget || this.stage.container().parentElement;
      // make sure the host can anchor absolutely-positioned children
      if (host && getComputedStyle(host).position === 'static') host.style.position = 'relative';

      const scale = this.stage.scaleX() || 1;

      // place textarea relative to the host only
      const place = (ta) => {
        // rect relative to the content layer, in stage units
        const r = target.getClientRect({ skipShadow: true, skipStroke: true, relativeTo: this.content });
        ta.style.left  = `${r.x * scale}px`;
        ta.style.top   = `${r.y * scale}px`;
        ta.style.width = `${r.width * scale}px`;
        ta.style.fontSize = `${target.fontSize() * scale}px`;
        ta.style.letterSpacing = `${(target.letterSpacing?.() || 0) * scale}px`;
        ta.style.padding = `${(target.padding?.() || 0) * scale}px`;
      };

      const ta = document.createElement("textarea");
      ta.value = target.text();
      Object.assign(ta.style, {
        position: "absolute",       // anchored to host
        background: "transparent",
        color: target.fill?.() || "#000",
        border: "none",
        outline: "none",
        resize: "none",
        overflow: "hidden",
        zIndex: 20,                  // above canvas, below sticky UI if any
        fontFamily: target.fontFamily(),
        fontWeight: target.fontStyle(),
        lineHeight: String(target.lineHeight() || 1.2),
        textAlign:  target.align?.() || "left",
        whiteSpace: "pre-wrap",
        transform: "translateZ(0)",
        WebkitTextSizeAdjust: "100%",
      });

      // append to host (not body)
      host.appendChild(ta);
      place(ta);
      ta.focus();
      ta.selectionStart = ta.selectionEnd = ta.value.length;

      // autosize + live mirror
      const autosize = () => {
        ta.style.height = "auto";
        ta.style.height = `${Math.min(ta.scrollHeight, 2000)}px`;
      };
      const mirror = () => {
        target.text(ta.value);
        this._updateFrame(target);
        this.content.batchDraw();
        target.fire("textChange");
        place(ta); // re-pin after height change
      };
      autosize(); mirror();
      ta.addEventListener("input", () => { autosize(); mirror(); });

      // keep pinned on window resize (orientation, etc.)
      const onResize = () => place(ta);
      window.addEventListener("resize", onResize);

      let closed = false;
      const finish = () => {
        if (closed) return;
        closed = true;
        this._finishInlineEdit = null;
        window.removeEventListener("resize", onResize);
        target.opacity(prevOpacity);
        if (target._frame) target._frame.visible(prevFrameVisible !== false);
        ta.remove();
        this.content.batchDraw();
        this._suppressAutosaveUntilInteraction = false;
        this._syncJson();
        this._updateFrame(target);
        this._select(target);
      };
      this._finishInlineEdit = finish;
      ta.addEventListener("keydown", (evt) => {
        if ((evt.key === "Enter") && (evt.metaKey || evt.ctrlKey)) { evt.preventDefault(); finish(); }
        else if (evt.key === "Escape") { evt.preventDefault(); finish(); }
      });
      ta.addEventListener("blur", finish);
    }

    // -------- Responsive fit --------
    _fit() {
        const hostW = this.stageHostTarget.clientWidth
        if (!hostW) return

        const calcH = hostW * (BASE_H / BASE_W)
        this.stage.width(hostW)
        this.stage.height(calcH)

        const scale = hostW / BASE_W
        this.stage.scale({ x: scale, y: scale })
        this.stage.batchDraw()
    }

    // -------- Bridge to left-side controls --------
    _bindControls() {
        // Font family
        this._bindInput("#font-family", (v) => this._applyToSelection(n => n.fontFamily(v)))
        // Font size (range)
        this._bindInput("#font-size", (v) => this._applyToSelection(n => n.fontSize(Number(v))))
        // Weight
        this._bindInput("#font-weight", (v) => this._applyToSelection(n => n.fontStyle(v)))
        // Color – either custom color input or hex box the picker writes to
        this._bindInput("#text-color", (v) => this._applyToSelection(n => n.fill(v)))
        this._bindInput("#text-color-hex", (v) => this._applyToSelection(n => n.fill(v)))
        // Text case (hidden input managed by your text-case controller)
        this._bindInput("#text-case", (v) => {
            const fn = CASE_FN[v] || ((s) => s)
            this._applyToSelection(n => n.text(fn(n.text())))
        })

        // Reset links (optional)
        this._bindClick("#reset-text-family", () => this._applyToSelection(n => n.fontFamily("Arial")))
        this._bindClick("#reset-text-size",   () => this._applyToSelection(n => n.fontSize(28)))
        this._bindClick("#reset-text-weight", () => this._applyToSelection(n => n.fontStyle("normal")))
        this._bindClick("#reset-text-color",  () => this._applyToSelection(n => n.fill("#1a2d70")))
        this._bindClick("#reset-text-case",   () => this._applyToSelection(n => n.text(n.text()))) // no-op
    }

    _bindInput(selector, handler) {
        const el = document.querySelector(selector)
        if (!el) return
        const apply = () => {
            this._suppressAutosaveUntilInteraction = false;   // ← mark interaction
            handler(el.value)
        }
        el.addEventListener("input", apply)
        el.addEventListener("change", apply)
    }

    _bindClick(selector, handler) {
        const el = document.querySelector(selector)
        if (!el) return
        el.addEventListener("click", (e) => {
            e.preventDefault()
            this._suppressAutosaveUntilInteraction = false   // ← mark interaction
            handler()
        })
    }

    _applyToSelection(mutator) {
        this._suppressAutosaveUntilInteraction = false;
        if (!this.selected || !(this.selected instanceof Konva.Text)) return
        mutator(this.selected)
        this.content.batchDraw()
        this._syncJson()
        this._reflectSelectionToPanel()
    }

    _applyAndReflect(mutator) {
        const n = this.selected
        if (!n || !(n instanceof Konva.Text)) return
        this._suppressAutosaveUntilInteraction = false;
        mutator(n)
        // remeasure → redraw → frame
        n.getClientRect({ skipShadow: true, skipStroke: true })
        n.setAttr('_boxWidth', n.width());
        this.content.batchDraw()
        // 2) update frame now …
        this._updateFrame(n)

        // 3) …and again next frame to catch final height
        requestAnimationFrame(() => this._updateFrame(n))
        this._emitInspect(n)      // notifies color/format panels
        this._reflectSelectionToPanel();
        this._updateRangeVisual('#font-size');
        this._syncJson()
    }

    _reflectSelectionToPanel() {
        // Paint current selection back into controls (lightweight)
        const n = this.selected
        if (!n || !(n instanceof Konva.Text)) return

        // --- FONT FAMILY: smart match against <option>s ---
        const family = n.fontFamily?.() || "Arial"
        const famSelect = document.querySelector("#font-family")

        if (famSelect) {
            let matched = false
            const target = family.toLowerCase()

            famSelect.querySelectorAll("option").forEach((opt) => {
                if (matched) return

                const v   = (opt.value || "").toLowerCase()
                const lbl = (opt.textContent || "").trim().toLowerCase()
                const df  = (opt.dataset.family || opt.dataset.font || "").toLowerCase()

                if ([v, lbl, df].some(str => str && str === target)) {
                    famSelect.value = opt.value
                    matched = true
                }
            })

            // If nothing matched, leave whatever is selected (default Arial)
        }

        // --- FONT SIZE (range + preview) ---
        const rawSize = n.fontSize?.() || 24;
        // Round to a sensible UI value so we don't show things like 17.9999999999
        const size = Math.round(rawSize * 10) / 10; // keep one decimal if ever needed

        setValueIfExists("#font-size", size);
        const sizeInput = document.querySelector('#font-size');
        if (sizeInput) sizeInput.value = size;
        this._updateRangeVisual('#font-size');

        const fsPreview = document.querySelector('#font-size-preview');
        if (fsPreview) {
            fsPreview.textContent = size;
        }

        // --- WEIGHT ---
        setValueIfExists("#font-weight", n.fontStyle?.() || "normal")

        // --- COLOR ---
        const hex = n.fill?.() || "#000000"
        setValueIfExists("#text-color-hex", hex)
        const colorInput = document.querySelector("#text-color")
        if (colorInput && /^#[0-9a-f]{6}$/i.test(hex)) {
            colorInput.value = hex
        }
        // NEW: reflect align + case (the text-format Stimulus listens to ie:inspect)
        const state = {
            align: n.align?.() || "center",
            case:  n.getAttr("_case") || "uppercase"
        }
        // document.dispatchEvent(new CustomEvent("ie:inspect", { detail: state }))

    }


    _selectFirstRoleText() {
        // Helper: true if node is a usable text node (not empty/invisible)
        function isUsableTextNode(node) {
            if (!node || !node.getStage?.()) return false;
            let txt = node.text?.();
            if (typeof txt !== "string" || txt.trim().length === 0) return false;
            // Check for near-zero height (e.g. invisible/empty)
            let rect = null;
            try {
                rect = node.getClientRect?.({ skipShadow: true, skipStroke: true, relativeTo: node.getLayer?.() || undefined });
            } catch(_) {}
            if (rect && typeof rect.height === "number" && rect.height < 4) return false;
            return true;
        }

        // 1. Try canonical roles in order, using cached and id lookup for each
        const roles = ["title", "when", "time", "where"];
        for (const role of roles) {
            const cached = this[`${role}Node`];
            const byId   = this.content?.findOne?.(`#${role}`);
            let node = cached && isUsableTextNode(cached) ? cached : (isUsableTextNode(byId) ? byId : null);
            if (isUsableTextNode(node)) {
                this._select(node);
                this._emitInspect(node);
                return;
            }
        }

        // 2. Fallback: any non-empty Text node
        const texts = this.content?.find?.("Text") || [];
        const firstUsable = texts.find(t => isUsableTextNode(t));
        if (firstUsable) {
            this._select(firstUsable);
            this._emitInspect(firstUsable);
            return;
        }
        // 3. Nothing usable: clear selection and emit generic event, but don't show empty frame
        this.selected = null;
        document.dispatchEvent(
            new CustomEvent("ie:inspect", { detail: { kind: "Text" } })
        );
    }

    //* Attaches all necessary interaction event listeners to the photoCarrier.
    //* This is the single source of truth for selecting, dragging, and transforming the photo.
    _attachPhotoListeners() {
        if (!this.photoCarrier) return;

        // Clear any previous listeners to prevent duplicates
        this.photoCarrier.off('click tap dragend transformend');

        // --- 1. Handle Selection ---
        this.photoCarrier.on('click tap', (e) => {
            e.cancelBubble = true;
            this._select(this.photoCarrier);
        });

        // --- 2. Handle Dragging ---
        // After a drag, we only need to save the new position.
        this.photoCarrier.on('dragend', () => {
            this._syncJson();
        });

        // --- 3. Handle Transforming (This is Point A!) ---
        // After a transform (resize/rotate), we must update the UI AND save the state.
        this.photoCarrier.on('transformend', () => {
            this._syncPhotoUi(); // THIS IS THE NEW LINE: Updates the side panel
            this._syncJson();   // Saves the new state to JSON
        });
    }

    // -------- Persist JSON --------
    _syncJson() {
        const json = this._dumpJson()
        if (!this.hasJsonFieldTarget) return
        this.jsonFieldTarget.value = this.stage.toJSON()
        const sink = this.jsonFieldTarget
        if (sink) sink.value = JSON.stringify(json)

        // NEW: debounce server autosave
        if (!this._suppressAutosaveUntilInteraction) {
            this._autosaveJsonDebounced(_normalizeForSave(json))
        }
    }
    _ensureFrameFor(node) {
        if (!node || !node.getStage?.()) return;
        if (node._frame) return

        node._frame = new Konva.Rect({
            stroke: '#4aa3ff',
            dash: [6, 4],
            cornerRadius: 6,
            listening: false,
            visible: true
        })

        // Insert the frame just *above* the node so it’s always visible
        this.content.add(node._frame)
        // node._frame.zIndex(node.index() + 1)
        node._frame.moveToTop() // always visible

        // auto-update frame whenever geometry changes
        const upd = () => this._updateFrame(node)
        node.on('textChange fontSizeChange fontFamilyChange fontStyleChange alignChange transform dragmove', upd)
    }
    _updateFrame(node) {
        if (!node || !node._frame || !node.getStage?.()) return;
        // Force remeasure, then read rect relative to the content layer
        node.getClientRect({ skipShadow: true, skipStroke: true })
        const r = node.getClientRect({ skipShadow: true, skipStroke: true, relativeTo: this.content })

        const pad = 6
        node._frame.position({ x: r.x - pad, y: r.y - pad })
        node._frame.size({ width: r.width + pad * 2, height: r.height + pad * 2 })
        // node._frame.visible(this.selected === node) if we want to hide other nodes
        this._refreshTransformer(node) // Keep Konva transformer box in syn
        this.content.batchDraw()
        if (this.selected === node) this._showMiniToolbarFor(node);
    }

    _setFrameActive(node, active) {
        if (!node?._frame) return
        node._frame.setAttrs({
            stroke: active ? '#1a2d70' : '#4aa3ff',
            dash:   active ? [8, 4]    : [6, 4],
            strokeWidth: active ? 2 : 1,
            opacity: active ? 1 : 0.7
        })
        node._frame.moveToTop()
    }


    // --- ANALYSIS ---------------------------------------------------------------

    async _analyzeBackground(imageEl) {
        // Build an offscreen canvas at a reasonable size (faster)
        const W = imageEl.naturalWidth || imageEl.width;
        const H = imageEl.naturalHeight || imageEl.height;
        const CW = SAMPLE_RES;
        const CH = Math.round(SAMPLE_RES * (H / W));

        const can = document.createElement("canvas");
        can.width = CW; can.height = CH;
        const ctx = can.getContext("2d", { willReadFrequently: true });
        ctx.drawImage(imageEl, 0, 0, CW, CH);
        const { data } = ctx.getImageData(0, 0, CW, CH);

        // Build two masks: holeMask (alpha < 10), busyMask (edge/variance)
        const holeMask = new Array(CH).fill(null).map(() => new Array(CW).fill(false));
        const busyMask = new Array(CH).fill(null).map(() => new Array(CW).fill(false));

        // Helper: index in data array
        const idx = (x, y) => (y * CW + x) * 4;

        // First pass: alpha / simple variance and gradient
        for (let y = 0; y < CH; y++) {
            for (let x = 0; x < CW; x++) {
                const o = idx(x, y);
                const r = data[o], g = data[o+1], b = data[o+2], a = data[o+3];
                if (a < 10) holeMask[y][x] = true;

                // very lightweight “busyness”: local gradient magnitude (|Δ|)
                let gx = 0, gy = 0;
                if (x+1 < CW) {
                    const o2 = idx(x+1, y);
                    gx = Math.abs(r - data[o2]) + Math.abs(g - data[o2+1]) + Math.abs(b - data[o2+2]);
                }
                if (y+1 < CH) {
                    const o3 = idx(x, y+1);
                    gy = Math.abs(r - data[o3]) + Math.abs(g - data[o3+1]) + Math.abs(b - data[o3+2]);
                }
                const edge = gx + gy;
                busyMask[y][x] = edge > BUSY_THRESH;
            }
        }

        // Mark the top band as busy (soft exclusion)
        const topRows = Math.floor(CH * TOP_BAND_FR);
        for (let y = 0; y < topRows; y++) {
            for (let x = 0; x < CW; x++) busyMask[y][x] = true;
        }

        // --- Compute preferred quiet band near the bottom (for when/time/where) ---
        // Examine the lower 40% of the image and pick the band with the lowest busy fraction.
        let preferredBottomYNorm = null;
        try {
            const startRow = Math.floor(CH * 0.60);
            const endRow   = CH - 1;
            const BAND_H   = Math.max(2, Math.floor(CH * 0.06)); // ~6% height per band
            let bestBusy = Infinity;
            let bestCenterRow = null;

            for (let y = startRow; y <= endRow - BAND_H; y += Math.max(1, Math.floor(BAND_H / 2))) {
                let probes = 0, hits = 0;
                for (let yy = y; yy < y + BAND_H; yy++) {
                    const row = busyMask[yy];
                    const holeRow = holeMask[yy];
                    if (!row) continue;
                    for (let xx = 0; xx < CW; xx++) {
                        // Treat hole pixels as busy to avoid photo cutouts
                        if (holeRow?.[xx]) { hits++; probes++; continue; }
                        probes++;
                        if (row[xx]) hits++;
                    }
                }
                if (!probes) continue;
                const frac = hits / probes; // 0..1 (lower is better)
                if (frac < bestBusy) { bestBusy = frac; bestCenterRow = y + BAND_H / 2; }
            }
            if (bestCenterRow != null) preferredBottomYNorm = bestCenterRow / CH; // 0..1
        } catch(_) {
            preferredBottomYNorm = null;
        }

        // --- Global simplicity metrics to detect "blankish" backgrounds ---
        let totalPixels = CW * CH;
        let holePixels  = 0;
        let busyPixels  = 0;
        for (let yy = 0; yy < CH; yy++) {
            const hr = holeMask[yy] || [];
            const br = busyMask[yy] || [];
            for (let xx = 0; xx < CW; xx++) {
                if (hr[xx]) holePixels++;
                if (br[xx]) busyPixels++;
            }
        }
        const holeFrac = totalPixels ? holePixels / totalPixels : 0;   // 0..1
        const busyFrac = totalPixels ? busyPixels / totalPixels : 0;   // 0..1
        // "blankish" if: mostly solid with *very* low texture OR mostly transparent
        //  - handles Make-Your-Own templates (big empty canvas) and user-provided flat images
        const blankish = (busyFrac < 0.015 && holeFrac < 0.1) || (holeFrac > 0.45);

        // Save for placement tests
        this._bgSample = { can, ctx, CW, CH, holeMask, busyMask, W, H, preferredBottomYNorm, blankish };
    }

    _intersectsForbidden(rect) {
        // 1) Safe margins (avoid top band & outer bleed in stage space)
        const SAFE = { top: 90, bottom: 30, left: 24, right: 24 };
        if (rect.y < SAFE.top) return true;
        if (rect.x < SAFE.left) return true;
        if ((rect.x + rect.w) > (BASE_W - SAFE.right)) return true;
        if ((rect.y + rect.h) > (BASE_H - SAFE.bottom)) return true;

        if (!this._bgSample) return false;
        const { CW, CH, holeMask, busyMask } = this._bgSample;

        // Map stage-space rect -> sample-space rect
        const sx = Math.floor(rect.x * CW / BASE_W);
        const sy = Math.floor(rect.y * CH / BASE_H);
        const sw = Math.max(1, Math.floor(rect.w * CW / BASE_W));
        const sh = Math.max(1, Math.floor(rect.h * CH / BASE_H));

        // Probe a sparse grid to keep it fast
        let holeHits = 0, probes = 0;
        const STEP = Math.max(2, Math.floor(Math.min(sw, sh) / 12));

        for (let yy = sy + 1; yy < sy + sh - 1; yy += STEP) {
            if (yy < 0 || yy >= CH) continue;
            for (let xx = sx + 1; xx < sx + sw - 1; xx += STEP) {
                if (xx < 0 || xx >= CW) continue;
                probes++;
                // Any hole pixel → forbid
                if (holeMask?.[yy]?.[xx]) { holeHits++; }
                // Optionally discourage very busy spots (soft block)
                // if (busyMask?.[yy]?.[xx]) return true; // uncomment to be stricter
            }
        }
        // If ≥20% of probes hit the hole, treat as forbidden (sits over photo area)
        if (probes > 0 && holeHits / probes >= 0.2) return true;

        return false;
    }

    // Return the bounding rect of the transparent "hole" in stage coords, or null.
    _holeRectStage(padPx = 6) {
        const s = this._bgSample;
        if (!s || !s.holeMask) return null;

        const { CW, CH, holeMask } = s;
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity, hits = 0;

        for (let y = 0; y < CH; y++) {
            const row = holeMask[y];
            if (!row) continue;
            for (let x = 0; x < CW; x++) {
                if (!row[x]) continue;
                hits++;
                if (x < minX) minX = x;
                if (x > maxX) maxX = x;
                if (y < minY) minY = y;
                if (y > maxY) maxY = y;
            }
        }

        // If too few pixels flagged, treat as no hole
        if (hits < 12 || !isFinite(minX)) return null;

        // Map sample → stage
        const cellW = BASE_W / CW;
        const cellH = BASE_H / CH;
        let x = Math.max(0, Math.floor(minX * cellW) - padPx);
        let y = Math.max(0, Math.floor(minY * cellH) - padPx);
        let w = Math.ceil((maxX - minX + 1) * cellW) + padPx * 2;
        let h = Math.ceil((maxY - minY + 1) * cellH) + padPx * 2;

        // Clamp to stage
        w = Math.min(w, BASE_W - x);
        h = Math.min(h, BASE_H - y);

        // Avoid degenerate
        if (w < 40 || h < 40) return null;

        return { x, y, w, h };
    }

    // Ensure a Konva.Group that clips its children to the hole rect.
// Returns the slot group (creates if needed). If no hole, builds a centered slot.
    _ensurePhotoSlot() {
        if (this.photoSlot && this.photoSlot.getStage()) return this.photoSlot;

        // If this invitation does not support photo uploads, never create/show a slot.
        if (this.hasPhotoEnabledValue && !this.photoEnabledValue) {
            this._hideSlotPrompt?.()
            return null
        }

        const hole = this._savedPhotoSlotRect || this._holeRectStage(16) || {
            x: Math.round(BASE_W * 0.2),
            y: Math.round(BASE_H * 0.22),
            w: Math.round(BASE_W * 0.6),
            h: Math.round(BASE_H * 0.42)
        };

        const slot = new Konva.Group({
            x: hole.x, y: hole.y, width: hole.w, height: hole.h, listening: true
        });
        if (window.matchMedia('(max-width: 576px)').matches) {
            slot.off('click tap')
            slot.on('click tap', () => this.choosePhoto())
        }
        const radiusFrac = 0.08;
        slot.clipFunc((ctx) => {
            const w = slot.width(), h = slot.height();
            const rr = Math.min(Math.min(w, h) * radiusFrac, w / 2, h / 2);
            ctx.beginPath();
            ctx.moveTo(rr, 0);
            ctx.arcTo(w, 0,   w, h, rr);
            ctx.arcTo(w, h,   0, h, rr);
            ctx.arcTo(0, h,   0, 0, rr);
            ctx.arcTo(0, 0,   w, 0, rr);
            ctx.closePath();
        });

        this.photoLayer.add(slot);
        // Do not moveToTop; preserve intended layer order

        this.photoSlot = slot;
        // If there's no photo yet, invite upload
        if (!this.photoCarrier && !this.photoNode) this._showSlotPrompt()
        this.photoLayer.batchDraw();
        return slot
    }

    // Return {w, h, x, y} for cover-fit inside slot {W,H} (image natural {iw, ih})
    _fitCover(iw, ih, W, H) {
        // cover scale
        let scale = Math.max(W / iw, H / ih);

        // add ~5% bleed so edges never show a gap
        scale *= 1.05;

        const w = Math.ceil(iw * scale);
        const h = Math.ceil(ih * scale);
        const x = Math.round((W - w) / 2);
        const y = Math.round((H - h) / 2);
        return { w, h, x, y, scale };
    }

    _collidesExisting(rect, blocks=[]) {
        return blocks.some(b => !(rect.x+rect.w < b.x || b.x+b.w < rect.x ||
            rect.y+rect.h < b.y || b.y+b.h < rect.y));
    }

// Scan candidate zones and pick the cleanest slot
    _findPlacement({ boxW, boxH, blocks = [], preferY = null, preferWeight = 0 }) {
        // Candidate scan step (falls back to your CANDIDATE_STEP if defined)
        const STEP = typeof CANDIDATE_STEP === "number" ? CANDIDATE_STEP : 16;

        // Compute scan bounds once (respect top band + safe margins).
        // NOTE: SAFE_MARGIN and TOP_BAND_FR are FRACTIONS (0..1). Adapt if yours are px.
        const left   = Math.floor(BASE_W * (typeof SAFE_MARGIN === "number" ? SAFE_MARGIN : 0.04));
        const right  = Math.floor(BASE_W * (1 - (typeof SAFE_MARGIN === "number" ? SAFE_MARGIN : 0.04)));
        const top    = Math.floor(Math.max(BASE_H * (typeof SAFE_MARGIN === "number" ? SAFE_MARGIN : 0.04),
            BASE_H * (typeof TOP_BAND_FR === "number" ? TOP_BAND_FR : 0.14)));
        const bottom = Math.floor(BASE_H * (1 - (typeof SAFE_MARGIN === "number" ? SAFE_MARGIN : 0.04)));

        const bounds = {
            x: left,
            y: top,
            w: Math.max(0, right - left),
            h: Math.max(0, bottom - top)
        };

        let best = null;

        // Measure how “busy” a rect is using  sampled masks (fraction 0..1).
        const busyFrac = (rect) => {
            if (!this._bgSample) return 0;
            const { CW, CH, busyMask, holeMask } = this._bgSample;

            const sx = Math.max(0, Math.floor(rect.x * CW / BASE_W));
            const sy = Math.max(0, Math.floor(rect.y * CH / BASE_H));
            const sw = Math.max(1, Math.floor(rect.w * CW / BASE_W));
            const sh = Math.max(1, Math.floor(rect.h * CH / BASE_H));

            let probes = 0, hits = 0;
            const step = Math.max(2, Math.floor(Math.min(sw, sh) / 12));

            for (let yy = sy; yy < Math.min(CH, sy + sh); yy += step) {
                for (let xx = sx; xx < Math.min(CW, sx + sw); xx += step) {
                    probes++;
                    if (holeMask?.[yy]?.[xx]) {      // treat hole like “busy” to strongly avoid
                        hits++;
                        continue;
                    }
                    if (busyMask?.[yy]?.[xx]) hits++;
                }
            }
            return probes ? (hits / probes) : 0;
        };

        // Scan candidates on a grid
        for (let y = bounds.y; y + boxH <= bounds.y + bounds.h; y += STEP) {
            for (let x = bounds.x; x + boxW <= bounds.x + bounds.w; x += STEP) {
                const rect = { x, y, w: boxW, h: boxH };

                if (this._intersectsForbidden(rect)) continue;
                if (this._collidesExisting?.(rect, blocks)) continue;

                // Score:
                //  - busy area (lower better)
                //  - vertical center preference (light)
                //  - horizontal center preference (lighter)
                //  - inner padding bonus (keeps away from edges a bit)
                //  - proximity to a preferred Y band if provided (lower is better)
                const b = busyFrac(rect); // 0..1

                const vCenter = Math.abs((y + boxH / 2) - BASE_H / 2) / (BASE_H / 2); // 0 center .. 1 edge
                const hCenter = Math.abs((x + boxW / 2) - BASE_W / 2) / (BASE_W / 2);

                const padLeft   = (x - bounds.x) / bounds.w;
                const padRight  = ((bounds.x + bounds.w) - (x + boxW)) / bounds.w;
                const padTop    = (y - bounds.y) / bounds.h;
                const padBottom = ((bounds.y + bounds.h) - (y + boxH)) / bounds.h;
                const edgeDist  = Math.max(0, Math.min(padLeft, padRight, padTop, padBottom)); // 0..~0.5

                let preferPenalty = 0;
                if (preferY != null) {
                    const yCenterNorm = (y + boxH / 2) / BASE_H;       // 0..1
                    preferPenalty = Math.abs(yCenterNorm - preferY) * (preferWeight || 0);
                }

                const score = (b * 1.0) + (vCenter * 0.20) + (hCenter * 0.10) - (edgeDist * 0.15) + preferPenalty;

                if (!best || score < best.score) best = { rect, score };
            }
        }

        // Fallback: small spiral from a preferred band (if given) or center
        if (!best) {
            let cx = bounds.x + bounds.w / 2 - boxW / 2;
            let cy = (preferY != null ? (preferY * BASE_H) : (bounds.y + bounds.h / 2)) - boxH / 2;
            for (let i = 0; i < 200; i++) {
                const rx = Math.max(bounds.x, Math.min(cx, bounds.x + bounds.w - boxW));
                const ry = Math.max(bounds.y, Math.min(cy, bounds.y + bounds.h - boxH));
                const rect = { x: rx, y: ry, w: boxW, h: boxH };
                if (!this._intersectsForbidden(rect) && !this._collidesExisting?.(rect, blocks)) {
                    return { x: rx, y: ry };
                }
                cx += (i % 2 === 0 ? STEP : -STEP);
                cy += (i % 3 === 0 ? STEP : -STEP);
            }
            // Last-resort: top-left inside bounds
            return { x: bounds.x, y: bounds.y };
        }

        return { x: best.rect.x, y: best.rect.y };
    }


    async _applyHintsOrAutoLayout() {
        // If our canonical nodes (title / when / time / where) already carry a
        // _hintY attribute, that means _initFromHints has created them from
        // editor_hints. In that case we only want to run the geometric
        // auto-layout (which respects _hintY), NOT re-apply hint styling.
        const hintedNodes = [this.titleNode, this.whenNode, this.timeNode, this.whereNode]
            .filter(n => n && n.getStage && n.getStage())
            .filter(n => typeof n.getAttr === "function" && typeof n.getAttr("_hintY") === "number");

        if (hintedNodes.length > 0) {
            // Just place them nicely (resolve overlaps, nudge off artwork, etc.)
            await this._autoLayout();
            return;
        }

        // Legacy path (no _initFromHints used): try to apply editor_hints directly.
        const usedHints = await this._applyEditorHints();
        if (!usedHints) {
            await this._autoLayout();
        }
    }

    async _applyEditorHints() {
        // Try Stimulus value first, then DOM datasets
        let raw = null;

        if (this.hasEditorHintsValue && this.editorHintsValue) {
            raw = this.editorHintsValue;
        } else if (this.element?.dataset?.editorHints) {
            raw = this.element.dataset.editorHints;
        } else if (this.stageHostTarget?.dataset?.editorHints) {
            raw = this.stageHostTarget.dataset.editorHints;
        } else {
            const host = this.stageHostTarget || this.element;
            if (host && typeof host.closest === "function") {
                const carrier = host.closest("[data-editor-hints]");
                if (carrier && carrier.dataset) {
                    raw = carrier.dataset.editorHints;
                }
            }
        }

        if (!raw) return false;

        // In case it’s HTML-escaped (e.g. &quot;)
        if (raw.indexOf("&quot;") !== -1 || raw.indexOf("&amp;") !== -1) {
            raw = raw.replace(/&quot;/g, '"').replace(/&amp;/g, '&');
        }

        let hints;
        try {
            hints = JSON.parse(raw);
            window.__ieHintsDebug = { raw, hints };
        } catch (e) {
            console.warn("Invalid editor_hints JSON", e, raw);
            return false;
        }

        const blocks = Array.isArray(hints.text_blocks) ? hints.text_blocks : [];
        if (!blocks.length) return false;

        // --- NEW: preload all hinted fonts so first draw uses the right families ---
        const fontsToLoad = [...new Set(
            blocks
                .map(b => (b && typeof b.font_family === "string" ? b.font_family.trim() : ""))
                .filter(f => f.length > 0)
        )];

        for (const fam of fontsToLoad) {
            try {
                await ensureFontLoaded(fam);
            } catch (e) {
                console.warn("[IE] failed to preload font from hints:", fam, e);
            }
        }
        // --- END NEW ---

        const byRole = {};
        blocks.forEach((b) => {
            if (!b || !b.role) return;
            byRole[b.role] = b;
        });

        if (!Object.keys(byRole).length) return false;

        // 🔴 NEW: remove canonical nodes for roles that are *not* in the hints
        const canonicalRoles = ["title", "when", "time", "where"];
        canonicalRoles.forEach((role) => {
            if (byRole[role]) return; // we have hints for this role, keep the node

            const prop = `${role}Node`;
            const node = this[prop];
            if (!node || !node.getStage?.()) return;

            // Kill its frame too
            if (node._frame) {
                node._frame.destroy();
                node._frame = null;
            }

            node.destroy();
            this[prop] = null;
        });

        const applyBlock = (node, role) => {
            const h = byRole[role];
            if (!node || !h) return;

            // 1) Apply sample text (from GPT) if provided
            if (typeof h.sample_text === "string" && h.sample_text.trim().length) {
                node.text(h.sample_text);
            }

            // 2) Apply font size from hints (NEW)
            if (typeof h.font_size === "number" && h.font_size > 0) {
                node.fontSize(h.font_size);
            }

            // 3) Width + height box
            const boxW =
                typeof h.w === "number" && h.w > 0
                    ? h.w
                    : Math.round(BASE_W * 0.86);

            const boxH =
                typeof h.h === "number" && h.h > 0
                    ? h.h
                    : node.fontSize() * (node.lineHeight?.() || 1.2) * 1.2;

            const x = typeof h.x === "number" ? h.x : node.x();
            const y = typeof h.y === "number" ? h.y : node.y();

            node.position({ x, y });
            node.width(boxW).setAttr("_boxWidth", boxW);

            if (h.align) node.align(String(h.align).toLowerCase());

            if (h.font_family) {
                node.fontFamily(h.font_family);
            }

            // Color from hints – and mark so auto-colors will never touch it
            if (h.color && typeof h.color === "string") {
                const hex = h.color.trim();
                if (/^#[0-9a-f]{6}$/i.test(hex)) {
                    node.fill(hex);
                    node.setAttr("_hintColor", hex);
                }
            }

            const centerY = y + boxH / 2;
            node.setAttr("_hintY", centerY / BASE_H);

            this._ensureFrameFor?.(node);
            this._updateFrame?.(node);
        };

        applyBlock(this.titleNode, "title");
        applyBlock(this.whenNode, "when");
        applyBlock(this.timeNode, "time");
        applyBlock(this.whereNode, "where");

        this.content.batchDraw();

        // Make sure hinted fonts + styles are painted immediately
        [ this.titleNode, this.whenNode, this.timeNode, this.whereNode ]
            .filter(Boolean)
            .forEach((n) => {
                n.getLayer()?.batchDraw();
            });

        // Gently nudge hinted boxes away from forbidden / busy areas
        // and from colliding with each other (cars-on-graphic case).
        // this._nudgeHintsOffArtwork?.();
        this._resolveHintOverlaps?.();

        // Now freeze further auto-layout / auto-color attempts
        this._layoutFrozenFromHints = true;

        // After hints are applied and layout nudged, schedule several font refresh passes
        try {
            this._scheduleFontRefresh?.();
        } catch (_) {}

        // After hints have potentially removed or repositioned canonical roles,
        // re-select the first usable text node so we don’t keep a stale
        // selection on a destroyed node (e.g., missing title role)
        try {
            this._selectFirstRoleText?.();
        } catch (_) {}
        return true;
    }

// --- LAYOUT -----------------------------------------------------------------

    async _autoLayout() {
        // If GPT editor hints were applied, respect them and do not re-layout.
        if (this._layoutFrozenFromHints) return;

        const title = this.titleNode;
        const when  = this.whenNode;
        const time  = this.timeNode;
        const where = this.whereNode;
        if (!title && !when && !where && !time) return;

        const hintY = (node, fallback) => {
            if (!node || typeof node.getAttr !== "function") return fallback;
            const v = node.getAttr("_hintY");
            return (typeof v === "number" && v >= 0 && v <= 1) ? v : fallback;
        };

        // Special-case: nearly blank canvases (Make-Your-Own etc.)
        if (this._bgSample?.blankish) {
            this._autoLayoutBlankish();
            return;
        }

        // Fixed text boxes so alignment doesn’t move them
        const usable = Math.round(BASE_W * 0.86);
        const estimateH = (n) => Math.max(n.fontSize()*1.4, n.fontSize() * (n.lineHeight() || 1.2) * 2);

        const placed = [];

        // 1) TITLE — prefer upper band (~20% down from top)
        if (title) {
            title.width(usable).setAttr("_boxWidth", usable).align(title.align() || "center");
            const p = this._findPlacement({
                boxW: usable,
                boxH: estimateH(title),
                blocks: placed,
                preferY: hintY(title, 0.20),
                preferWeight: 1.5
            });
            title.position(p);
            placed.push({ x: p.x, y: p.y, w: usable, h: estimateH(title) });
            this._updateFrame(title);
        }

        // 2) WHERE — prefer lower band (~82% down from top)
        if (where) {
            where.width(usable).setAttr("_boxWidth", usable).align(where.align() || "center");
            const fallbackBottom = (this._bgSample?.preferredBottomYNorm ?? 0.82);
            const p = this._findPlacement({
                boxW: usable,
                boxH: estimateH(where),
                blocks: placed,
                preferY: hintY(where, fallbackBottom),
                preferWeight: 2.2
            });
            where.position(p);
            placed.push({ x: p.x, y: p.y, w: usable, h: estimateH(where) });
            this._updateFrame(where);
        }

        // 3) WHEN — prefer middle band (~50%)
        if (when) {
            when.width(usable).setAttr("_boxWidth", usable).align(when.align() || "center");
            const fallbackMid = (this._bgSample?.preferredBottomYNorm ?? 0.50);
            const p = this._findPlacement({
                boxW: usable,
                boxH: estimateH(when),
                blocks: placed,
                preferY: hintY(when, fallbackMid),
                preferWeight: 1.8
            });
            when.position(p);
            placed.push({ x: p.x, y: p.y, w: usable, h: estimateH(when) });
            this._updateFrame(when);
        }

        // 4) TIME - prefer stacking under WHEN; fallback to findPlacement
        if (time) {
            time.width(usable).setAttr("_boxWidth", usable).align(time.align() || "center");

            let placedTime = false;
            if (when) {
                const wantX = when.x();
                const wantW = when.width();
                const wantY = when.y() + estimateH(when) * 1.25; // gentle spacing below WHEN
                const candidate = { x: wantX, y: wantY, w: wantW, h: estimateH(time) };

                const withinBounds = candidate.x >= 0 && candidate.y >= 0 &&
                    (candidate.x + candidate.w) <= BASE_W && (candidate.y + candidate.h) <= BASE_H;

                if (withinBounds && !this._intersectsForbidden(candidate) && !this._collidesExisting(candidate, placed)) {
                    time.position({ x: wantX, y: wantY });
                    placed.push(candidate);
                    this._updateFrame(time);
                    placedTime = true;
                }
            }

            if (!placedTime) {
                const fallbackTime = (this._bgSample?.preferredBottomYNorm ?? 0.57);
                const p = this._findPlacement({
                    boxW: usable,
                    boxH: estimateH(time),
                    blocks: placed,
                    preferY: hintY(time, fallbackTime),
                    preferWeight: 1.8
                });
                time.position(p);
                placed.push({ x: p.x, y: p.y, w: usable, h: estimateH(time) });
                this._updateFrame(time);
            }
        }

        // After placing, choose readable colors – but skip nodes that have a hint color
        const colorTargets = [title, when, time, where]
            .filter(Boolean)
            .filter(n => !n.getAttr || !n.getAttr("_hintColor"));
        if (colorTargets.length) {
            this._applyAutoColors(colorTargets);
        }
    }

    _autoLayoutBlankish() {
        const title = this.titleNode;
        const when  = this.whenNode;
        const time  = this.timeNode;
        const where = this.whereNode;

        const usable = Math.round(BASE_W * 0.86);
        const leftX  = Math.round(BASE_W * 0.07);

        const estimateH = (n) => {
            if (!n) return 0;
            return Math.max(n.fontSize()*1.4, n.fontSize() * (n.lineHeight() || 1.2) * 1.6);
        };

        // Vertical rhythm for nearly-blank canvases:
        //   when (date) near top, title under it, where near lower-mid, time at bottom band.
        let y = Math.round(BASE_H * 0.08);

        if (when) {
            when.width(usable).setAttr("_boxWidth", usable).align(when.align() || "center");
            when.position({ x: leftX, y });
            this._updateFrame(when);
            y += estimateH(when) + Math.round(BASE_H * 0.04);
        }

        if (title) {
            title.width(usable).setAttr("_boxWidth", usable).align(title.align() || "center");
            title.position({ x: leftX, y });
            this._updateFrame(title);
        }

        // Lower placements
        if (where) {
            const yWhere = Math.round(BASE_H * 0.70);
            where.width(usable).setAttr("_boxWidth", usable).align(where.align() || "center");
            where.position({ x: leftX, y: yWhere });
            this._updateFrame(where);
        }

        if (time) {
            // Sit in the lower band with generous spacing from bottom
            const yTime = Math.round(BASE_H * 0.86);
            time.width(usable).setAttr("_boxWidth", usable).align(time.align() || "center");
            time.position({ x: leftX, y: yTime });
            this._updateFrame(time);
        }

        // Choose legible colors against mostly-flat backgrounds
        this._applyAutoColors([title, when, time, where].filter(Boolean));
        this.content.batchDraw();
    }

    _hexToRgb(hex) {
        const m = hex.replace("#","").padEnd(6,"0").match(/.{1,2}/g);
        const [r,g,b] = m.map(v => parseInt(v,16));
        return { r, g, b };
    }
    _rgbToHsl({r,g,b}) {
        r/=255; g/=255; b/=255;
        const max=Math.max(r,g,b), min=Math.min(r,g,b);
        let h,s,l=(max+min)/2;
        if (max===min) { h=s=0; }
        else {
            const d=max-min;
            s=l>0.5 ? d/(2-max-min) : d/(max+min);
            switch(max){
                case r: h=(g-b)/d+(g<b?6:0); break;
                case g: h=(b-r)/d+2; break;
                case b: h=(r-g)/d+4; break;
            }
            h/=6;
        }
        return { h: h*360, s, l };
    }
    _lum(hex) {
        const toLin = (c) => {
            const v = parseInt(c,16)/255;
            return v <= 0.03928 ? v/12.92 : Math.pow((v+0.055)/1.055, 2.4);
        };
        const m = hex.replace("#","").padEnd(6,"0").match(/.{1,2}/g);
        const [r,g,b] = m.map(toLin);
        return 0.2126*r + 0.7152*g + 0.0722*b;
    }
    _contrast(hex1, hex2) {
        const L1 = this._lum(hex1), L2 = this._lum(hex2);
        const [a,b] = L1 > L2 ? [L1,L2] : [L2,L1];
        return (a + 0.05) / (b + 0.05);
    }
    _colorfulness({r,g,b}) {
        // simple chroma proxy: max-min range in 0..1
        const max = Math.max(r,g,b), min = Math.min(r,g,b);
        return (max - min) / 255;
    }

    _sampleAvgColor(rect) {
        if (!this._bgSample) return "#f2f2f2";
        const { ctx, CW, CH, holeMask } = this._bgSample;

        // Map stage-space rect -> sample-space rect
        const sx = Math.max(0, Math.floor(rect.x * CW / BASE_W));
        const sy = Math.max(0, Math.floor(rect.y * CH / BASE_H));
        const sw = Math.max(1, Math.floor(rect.w * CW / BASE_W));
        const sh = Math.max(1, Math.floor(rect.h * CH / BASE_H));

        let r=0, g=0, b=0, n=0;
        const STEP = Math.max(2, Math.floor(Math.min(sw, sh) / 14));

        for (let yy = sy; yy < Math.min(CH, sy + sh); yy += STEP) {
            for (let xx = sx; xx < Math.min(CW, sx + sw); xx += STEP) {
                if (holeMask?.[yy]?.[xx]) continue; // skip the transparent hole
                const img = ctx.getImageData(xx, yy, 1, 1).data;
                const a = img[3];
                if (a < 230) continue;               // ignore semi/transparent pixels
                r += img[0]; g += img[1]; b += img[2]; n++;
            }
        }
        if (!n) return "#f2f2f2";
        const rr = Math.round(r/n).toString(16).padStart(2,"0");
        const gg = Math.round(g/n).toString(16).padStart(2,"0");
        const bb = Math.round(b/n).toString(16).padStart(2,"0");
        return `#${rr}${gg}${bb}`;
    }

    // Force all Text nodes to re-measure using the *loaded* webfonts.
    _refreshTextFontRendering() {
        if (!this.content || typeof this.content.find !== "function") return;

        const texts = this.content.find("Text") || [];
        texts.forEach((n) => {
            if (!n || !n.getStage?.()) return;

            // Re-apply the current family so Konva recomputes metrics
            const fam = typeof n.fontFamily === "function" ? n.fontFamily() : null;
            if (fam) {
                n.fontFamily(fam);
            }

            // If Konva has any internal cache, clear it
            if (typeof n.clearCache === "function") {
                n.clearCache();
            }

            // Force a fresh bounding box; keeps frames in sync
            try {
                n.getClientRect({ skipShadow: true, skipStroke: true });
            } catch (_) {}

            this._updateFrame?.(n);
        });

        // Redraw the canvas
        try {
            this.content.batchDraw();
        } catch (_) {}
        try {
            this.stage?.batchDraw?.();
        } catch (_) {}

        // Keep sidebar in sync with whatever is currently selected
        if (this.selected &&
            this.selected.getStage?.() &&
            this.selected.getClassName?.() === "Text") {
            this._emitInspect?.(this.selected);
        }
    }

    _scheduleFontRefresh() {
        // Run several passes so we catch fonts + text nodes regardless of async timing
        const attempts = [0, 150, 400, 900];

        attempts.forEach((ms) => {
            setTimeout(() => {
                if (!this.stage || this.stage.isDestroyed?.()) return;
                if (typeof this._refreshTextFontRendering === "function") {
                    this._refreshTextFontRendering();
                }
            }, ms);
        });
    }

    _applyAutoColors(nodes) {
        nodes.forEach(n => {
            // If GPT gave us a color, never override it
            if (n.getAttr && n.getAttr("_hintColor")) {
                return;
            }
            const rect  = { x: n.x(), y: n.y(), w: n.width(), h: Math.max(n.height(), n.fontSize()*1.4) };
            const bgHex = this._sampleAvgColor(rect);

            const Lbg = this._lum(bgHex); // 0..1
            const preferredFirst = Lbg >= 0.70 ? ["#111111", "#1a2d70", "#333333"]
                : (Lbg <= 0.30 ? ["#ffffff"] : []);

            // Try the preferred list first (highest contrast among them)
            if (preferredFirst.length) {
                const bestPref = preferredFirst
                    .map(hex => ({ hex, cr: this._contrast(hex, bgHex) }))
                    .filter(x => x.cr >= 4.5)
                    .sort((a,b) => b.cr - a.cr)[0];
                if (bestPref) {
                    n.fill(bestPref.hex);
                    if (bestPref.cr < 7) {
                        n.shadowEnabled(true);
                        n.shadowColor("#000");
                        n.shadowBlur(6);
                        n.shadowOpacity(0.22);
                        n.shadowOffset({ x: 0, y: 2 });
                    } else {
                        n.shadowEnabled(false);
                    }
                    this._emitInspect(n);
                    return; // skip generic palette scan for this node
                }
            }
            const bgRgb = this._hexToRgb(bgHex);
            const bgHsl = this._rgbToHsl(bgRgb);

            let best = null;

            AUTO_COLOR_PALETTE.forEach((hex, idx) => {
                const cr = this._contrast(hex, bgHex);
                if (cr < 4.5) return;

                const rgb = this._hexToRgb(hex);
                const cf  = this._colorfulness(rgb);                  // 0..1
                const hsl = this._rgbToHsl(rgb);
                let hueDelta = Math.abs(hsl.h - bgHsl.h);
                hueDelta = hueDelta > 180 ? 360 - hueDelta : hueDelta;

                const neutral = (hex === "#000000" || hex === "#ffffff" || hex === "#333333");
                const neutralPenalty = neutral ? 5.0 : 0.0;          // ↑ discourage white/black/gray
                const brandBias = (!neutral && idx <= 5) ? 0.35 : 0;  // tiny + for brand colors

                const score = (cr * 1.0) + (cf * 1.0) + ((hueDelta/180) * 1.0) + brandBias - neutralPenalty;
                if (!best || score > best.score) best = { hex, cr, score };
            });

            if (!best) {
                // Fallback: maximum contrast
                best = AUTO_COLOR_PALETTE
                    .map(hex => ({ hex, cr: this._contrast(hex, bgHex) }))
                    .sort((a,b)=>b.cr-a.cr)[0] || { hex: n.fill() || "#111111", cr: 4.5 };
            }

            n.fill(best.hex);
            if (best.cr < 7) {
                n.shadowEnabled(true);
                n.shadowColor("#000");
                n.shadowBlur(6);
                n.shadowOpacity(0.22);
                n.shadowOffset({ x: 0, y: 2 });
            } else {
                n.shadowEnabled(false);
            }
            this._emitInspect(n)
        });
        this.content.batchDraw();
    }

    async _deletePhoto() {
        // Best-effort request to remove from Cloudflare
        // try {
        //     if (this._photoImageId) {
        //         fetch(`/cf_images/${encodeURIComponent(this._photoImageId)}`, { method: "DELETE", credentials: "same-origin" })
        //             .catch(() => {});
        //     }
        // } catch (_) {}

        if (this.photoCarrier) { this.photoCarrier.destroy(); this.photoCarrier = null; }
        if (this.photoNode)    { this.photoNode.destroy();    this.photoNode = null; }

        this._photoImageId = null;
        this._photoUrl = null;

        this._ensurePhotoSlot();
        this._showSlotPrompt();
        this.photoLayer?.batchDraw();
        document.dispatchEvent(new CustomEvent("ie:inspect", { detail: { kind: "Text" } }));
        this._syncJson();
    }

    _setPhotoSizePercent(pct = 100) {
        if (!this.photoCarrier) return;

        // Your existing logic to calculate scale is good
        const scale = Math.max(0.0, Math.min(3.0, pct / 100));

        this.photoCarrier.scale({ x: scale, y: scale });
        this.photoLayer?.batchDraw();
        this._syncJson();
    }

    _setPhotoRotation(deg = 0) {
        if (!this.photoCarrier) return;

        this.photoCarrier.rotation(deg);
        this.photoLayer?.batchDraw();
        this._syncJson();
    }

    _applyPhotoFilter(name = "") {
        if (!this.photoCarrier) return;

        this._photoFilter = name || ""; // Store the filter name
        this._setPhotoFilterLocal(this._photoFilter); // Apply the Konva filter
        this._syncJson();
    }

    _setPhotoFilterLocal(name = "") {
        if (!this.photoNode) return;
        // Always clear previous cache before re-caching to avoid size/scale artifacts
        try { this.photoNode.clearCache(); } catch(_) {}
        if (!name) {
            this.photoNode.filters([]);
            this.photoLayer?.batchDraw();
            return;
        }
        this.photoNode.cache({ pixelRatio: 1 });
        const KN = Konva.Filters;
        const filters = [];

        // reset
        this.photoNode.brightness(0);
        this.photoNode.contrast(0);
        this.photoNode.blurRadius(0);
        this.photoNode.pixelSize(0);
        this.photoNode.noise(0);

        switch (name) {
            case "grayscale":   filters.push(KN.Grayscale); break;
            case "sepia":       filters.push(KN.Sepia); break;
            case "contrast":    filters.push(KN.Contrast);    this.photoNode.contrast(0.5); break;
            case "brightness":  filters.push(KN.Brighten);    this.photoNode.brightness(0.2); break;
            case "blur":        filters.push(KN.Blur);        this.photoNode.blurRadius(5); break;
            case "pixelate":    filters.push(KN.Pixelate);    this.photoNode.pixelSize(6); break;
            case "noise":       filters.push(KN.Noise); break;
            case "kaleidoscope":filters.push(KN.Kaleidoscope);this.photoNode.kaleidoscopePower(3); this.photoNode.kaleidoscopeAngle(0); break;
        }
        this.photoNode.filters(filters);
        this.photoLayer?.batchDraw();
    }

    // Show an inline "Upload photo" prompt inside the photo slot (when empty)
    _showSlotPrompt() {

        if (this.hasPhotoEnabledValue && !this.photoEnabledValue) return

        const slot = this.photoSlot
        if (!slot || this._slotPrompt) return

        // Basic token of “mobile” (tweak breakpoint if you want)
        const isMobile = window.innerWidth <= 576

        // Build: Group(Rect + Text)
        const pill = new Konva.Group({ listening: true, id: "ieSlotPrompt" })

        // Text style (match your brand)
        const txt = new Konva.Text({
            text: '🤳  Upload photo',
            fontFamily: 'Manrope',
            fontSize: isMobile ? 14 : 16,   // responsive-ish
            fontStyle: '700',                // bold
            fill: '#0c659e',
            padding: 10
        })

        // Padding per your button: 12px vertical, 24px horizontal
        const PAD_X = 10
        const PAD_Y = 5
        // Measure text first
        const tr = txt.getClientRect({ skipStroke: true, skipShadow: true })
        const pillW = Math.ceil(tr.width + PAD_X * 2)
        const pillH = Math.ceil(tr.height + PAD_Y * 2)

        // Rounded rect background
        const bg = new Konva.Rect({
            width: pillW,
            height: pillH,
            fill: '#daf1fd',
            cornerRadius: 12,
            shadowColor: 'black',
            shadowBlur: 6,
            shadowOpacity: 0.15
            // no stroke to match your button (you had border:none)
        })

        // Center text in rect with padding
        txt.position({
            x: PAD_X,
            y: Math.round((pillH - tr.height) / 2) // vertical centering
        })

        pill.add(bg)
        pill.add(txt)
        slot.add(pill)

        // Center the whole pill inside the slot, avoiding overlap with text nodes
        const centerPill = () => {
            const w = slot.width(), h = slot.height();

            // Try center, then upper third, then lower third inside the slot
            const candidates = [
                { x: w / 2, y: h / 2 },
                { x: w / 2, y: h * 0.25 },
                { x: w / 2, y: h * 0.75 }
            ];

            const texts = this.content?.find?.("Text") || [];

            const overlapsText = (pos) => {
                if (!texts.length || !this.stage) return false;

                // Convert pill position inside the slot -> stage coordinates
                const stageX = slot.x() + pos.x - pillW / 2;
                const stageY = slot.y() + pos.y - pillH / 2;
                const pillRect = { x: stageX, y: stageY, w: pillW, h: pillH };

                return texts.some((t) => {
                    if (!t.getStage?.()) return false;
                    const r = t.getClientRect({ skipStroke: true, skipShadow: true });
                    return !(
                        pillRect.x + pillRect.w < r.x ||
                        r.x + r.width < pillRect.x ||
                        pillRect.y + pillRect.h < r.y ||
                        r.y + r.height < pillRect.y
                    );
                });
            };

            let chosen = candidates[0];
            for (const c of candidates) {
                if (!overlapsText(c)) {
                    chosen = c;
                    break;
                }
            }

            pill.position(chosen);
            pill.offset({ x: pillW / 2, y: pillH / 2 });
        };
        centerPill()
        // make sure the pill is visible even after rehydrate
        pill.moveToTop();
        // Do not move slot or layer to top; keep layer order as is
        this.photoLayer?.batchDraw?.();

        slot.on("mouseenter", () => {
            if (!this.photoCarrier && !this.photoNode) {
                slot.getStage().container().style.cursor = "pointer"
            }
        })
        slot.on("mouseleave", () => {
            slot.getStage().container().style.cursor = "default"
        })
        // Click → open file picker
        pill.off('click tap')
        pill.on('click tap', (e) => {
            e.cancelBubble = true
            this.choosePhoto()
        })

        // Also make the *empty* slot a trigger
        slot.off('click tap')
        slot.on('click tap', (e) => {
            // only when empty
            if (!this.photoCarrier && !this.photoNode) this.choosePhoto()
        })

        // Keep a handle
        this._slotPrompt = pill

        // Recenter on resize
        this._slotPromptResizeHandler ||= () => {
            // You can tweak font for mobile here if you want it more dynamic:
            // txt.fontSize(window.innerWidth <= 576 ? 18 : 23)
            // Recompute measurements if you change font size dynamically
            centerPill()
            this.photoLayer?.batchDraw()
        }
        window.addEventListener('resize', this._slotPromptResizeHandler)

        this.photoLayer?.batchDraw()
    }

    /**
     * Reads the current state of the photo from the canvas
     * and updates all corresponding UI controls in the side panel.
     */
    _syncPhotoUi() {
        if (!this.photoCarrier) return;

        const photoNode = this.photoCarrier;
        const rotation = photoNode.rotation() || 0;
        const scale = photoNode.scaleX() || 1; // Assuming scale is uniform
        const filterName = this._photoFilter || "";

        // --- 1. Sync Rotation ---
        const rotationInput = document.getElementById('photo-rotation');
        if (rotationInput) {
            rotationInput.value = Math.round(rotation);
        }

        // --- 2. Sync Size ---
        const sizeSlider = document.getElementById('photo-size');
        if (sizeSlider) {
            const sizePercent = Math.round((this.photoCarrier.scaleX() || 1) * 100);

            // Step 1: Set the slider's value programmatically.
            sizeSlider.value = sizePercent;

            // Step 2: Directly tell the progress-bar-controller to update its visuals.
            // This is the correct, loop-free way to do this.
            const progressBarElement = sizeSlider.closest('[data-controller="progress-bar"]');
            if (progressBarElement) {
                const progressBarController = this.application.getControllerForElementAndIdentifier(progressBarElement, "progress-bar");
                if (progressBarController) {
                    // Manually update the text display, as it's part of the controller's targets
                    if (progressBarController.hasValueDisplayTarget) {
                        progressBarController.valueDisplayTarget.textContent = `${sizePercent}${progressBarElement.dataset.unit || '%'}`;
                    }
                    // Now, call the update method to redraw the blue bar
                    progressBarController.update();
                }
            }
        }

        // --- 3. Sync Filter (with Choices.js support) ---
        try {
            const choicesInstance = (window.choicesRegistry || {})["photoFilter"];
            if (choicesInstance) {
                // Use the Choices.js API to set the value without triggering its own change event
                choicesInstance.setChoiceByValue(filterName);
            } else {
                // Fallback for standard select
                const filterSelect = document.getElementById('photo-filter');
                if (filterSelect) filterSelect.value = filterName;
            }
        } catch (e) {
            console.warn("Could not sync photo filter UI.", e);
        }
    }

    _hideSlotPrompt() {
        if (!this._slotPrompt) return
        this._slotPrompt.destroy()
        this._slotPrompt = null
        if (this._slotPromptResizeHandler) {
            window.removeEventListener('resize', this._slotPromptResizeHandler)
            this._slotPromptResizeHandler = null
        }
        this.photoLayer?.batchDraw()
    }

    _refreshTransformer(node) {
        if (!this.transformer || !node || !node.getStage?.()) return;

        // current selection?
        let selectedNodes = [];
        if (typeof this.transformer.nodes === 'function') {
            selectedNodes = this.transformer.nodes();
        } else if (typeof this.transformer.node === 'function') {
            const n = this.transformer.node();
            if (n) selectedNodes = [n];
        }

        const nodeLayer = node.getLayer?.();
        if (nodeLayer && this.transformer.getParent() !== nodeLayer) {
            this.transformer.remove();
            nodeLayer.add(this.transformer);
        }
        if (selectedNodes && selectedNodes.includes(node)) {
            try {
                this.transformer.forceUpdate?.();
                (this.transformer.getLayer?.() || this.uiLayer)?.batchDraw();
                requestAnimationFrame(() => {
                    try {
                        this.transformer.forceUpdate?.();
                        (this.transformer.getLayer?.() || this.uiLayer)?.batchDraw();
                    } catch {}
                });
            } catch {}
        }
    }


    _updateRangeVisual(selector) {
        const el = document.querySelector(selector);
        if (!el) return;
        const min = Number(el.min || 0);
        const max = Number(el.max || 100);
        const val = Number(el.value || min);
        const pct = (max > min) ? ((val - min) / (max - min)) * 100 : 0;
        el.style.setProperty('--range-progress', `${pct}%`);
    }
    // --- Mini toolbar (HTML overlay) ---------------------------------------
    _buildMiniToolbar() {
        // const host = this.stageHostTarget || this.element;
        // const bar = document.createElement('div');
        // bar.style.position = 'absolute';
        // bar.style.top = '0';
        // bar.style.left = '0';
        // bar.style.transform = 'translate(-50%, -100%)';
        // bar.style.display = 'none';
        // bar.style.zIndex = '20';
        // // single round delete button
        // const btn = document.createElement('button');
        // btn.type = 'button';
        // btn.title = 'Delete';
        // Object.assign(btn.style, {
        //     width: '32px', height: '32px', borderRadius: '50%',
        //     border: 'none', boxShadow: '0 2px 6px rgba(0,0,0,.15)',
        //     background: '#ffffff', cursor: 'pointer'
        // });
        // btn.innerHTML = '<i class="bi bi-trash"></i>';
        // btn.addEventListener('click', () => this._deleteSelected());
        // bar.appendChild(btn);
        // // container for absolute positioning
        // host.style.position = host.style.position || 'relative';
        // host.appendChild(bar);
        // this._miniBar = bar;
        // Reuse the old property name so _showMiniToolbarFor / _hideMiniToolbar
        // / _destroyMiniToolbar keep working.
        if (this._miniBar) return;

        // Host = the element that wraps the Konva stage (inside #editor)
        const host = this.stageHostTarget || this.element;
        if (!host) return;

        // Make sure the host can be an absolute-positioning context
        if (!host.style.position || host.style.position === "static") {
            host.style.position = "relative";
        }

        const el = document.createElement("div");
        el.className = "ie-mini-toolbar";

        el.innerHTML = `
    <button type="button" class="ie-mini-btn ie-mini-edit" data-role="edit" title="Edit text" aria-label="Edit selected text">
      Edit text
    </button>
    <button type="button" class="ie-mini-btn" data-role="duplicate" title="Duplicate text">
      <i class="bi bi-files"></i>
    </button>
    <button type="button" class="ie-mini-btn" data-role="delete" title="Delete">
      <i class="bi bi-trash"></i>
    </button>
  `;

        host.appendChild(el);
        this._miniBar = el;

        const dupBtn = el.querySelector('[data-role="duplicate"]');
        const delBtn = el.querySelector('[data-role="delete"]');
        el.querySelector('[data-role="edit"]')?.addEventListener("click", (e) => {
            e.preventDefault();
            if (this.selected?.getClassName?.() === "Text") this._inlineEdit(this.selected);
        });

        if (dupBtn) dupBtn.addEventListener("click", (e) => {
            e.preventDefault();
            this._duplicateSelected();
        });

        if (delBtn) delBtn.addEventListener("click", (e) => {
            e.preventDefault();
            this._deleteSelected();
        });
    }

    _destroyMiniToolbar() {
        if (this._miniBar) { this._miniBar.remove(); this._miniBar = null; }
    }

    _hideMiniToolbar() {
        if (this._miniBar) this._miniBar.style.display = 'none';
    }

    _showMiniToolbarFor(node) {
        if (!this._miniBar || !node) return;
        const editButton = this._miniBar.querySelector('[data-role="edit"]');
        if (editButton) editButton.hidden = node.getClassName?.() !== "Text";
        // compute the top-middle of the node in page coords, then map into host
        const hostBox = this.stage.container().getBoundingClientRect();
        const rect = node.getClientRect({
            skipShadow: true,
            skipStroke: true,
            relativeTo: node.getLayer?.() || this.content
        });
        const scale = this.stage.scaleX() || 1;
        const isSticker = node?.name?.() === 'sticker';
        const x = hostBox.left + (rect.x + rect.width / 2) * scale + (isSticker ? -24 : 0);
        const y = hostBox.top + rect.y * scale; // top edge
        // place the bar
        this._miniBar.style.display = 'flex';
        // Keep the wider text-edit control reachable near the canvas edges on phones.
        const halfWidth = this._miniBar.offsetWidth / 2;
        const left = Math.max(halfWidth + 4, Math.min(hostBox.width - halfWidth - 4, x - hostBox.left));
        const top = Math.max(this._miniBar.offsetHeight + 4, y - hostBox.top - 8);
        this._miniBar.style.left = left + 'px';
        this._miniBar.style.top = top + 'px';
    }


    _deleteSelected() {
        const n = this.selected;
        if (!n) return;
        // Photo special-case uses existing helper
        if (n === this.photoCarrier || n === this.photoNode) {
            this._deletePhoto();
            this._hideMiniToolbar();
            this.selected = null;
            return;
        }
        // If a text frame exists, remove it too
        if (n._frame) { n._frame.destroy(); n._frame = null; }
        // Destroy node and sync
        n.destroy();
        if (n === this.titleNode) this.titleNode = null;
        if (n === this.whenNode)  this.whenNode  = null;
        if (n === this.timeNode)  this.timeNode  = null;
        if (n === this.whereNode) this.whereNode = null;
        this.content?.batchDraw();
        this.stickersLayer?.batchDraw();
        this.uiLayer?.batchDraw();
        this._hideMiniToolbar();
        this.transformer.nodes([]);
        this.selected = null;
        // If no Text nodes left, show the “Add sample text” button
        this._updateEmptyStateVisibility();
        this._syncJson();
        // switch panel back to Text tools by default
        document.dispatchEvent(new CustomEvent('ie:inspect', { detail: { kind: 'Text' } }));
    }

    _duplicateSelected() {
        const src = this.selected;
        if (!src || src.getClassName?.() !== "Text") return;

        // Clone the node but strip any special id/name so it becomes a plain text node
        const clone = src.clone({
            id: `text-${Date.now()}`, // ensure unique
            name: "text"              // generic, not title/when/where/time
        });

        // Remove any custom role-ish attrs if you have them
        clone.setAttr("_role", null);

        // Offset a bit so it doesn’t sit exactly on top
        clone.position({
            x: src.x() + 10,
            y: src.y() + 10
        });

        this.content.add(clone);

        // Wire up interactions for this *specific* node
        this._wireTextNode(clone);

        // Ensure it has its own frame + selection
        this._ensureFrameFor(clone);
        this._updateFrame(clone);
        this._select(clone);

        this.content.batchDraw();
        this._syncJson();
    }

    async _createOrReuseDraft(snapshot = null) {
        if (this.contextValue !== "draft" || this._editorDisconnected) return;
        if (this.draftId) return;
        if (this._creatingDraftPromise) return this._creatingDraftPromise;

        const body = {
            invitation_slug: this.invitationSlug,
            base_width:  BASE_W,
            base_height: BASE_H,
            // optional: include current state on first create so server has content
            editor_json: snapshot || _normalizeForSave(this._dumpJson()),
            ...this._partyPlaceDraftContext()
        };

        // If charity and exclusive path is preselected
        if (this.hasCharityIdValue && this.charityIdValue) {
            body.fields_json = { party: { charity_id: Number(this.charityIdValue) } };

            // ALSO send top-level charity_id for draft-level locking
            body.charity_id = Number(this.charityIdValue);
        }

        if (this.hasExclusiveValue && this.exclusiveValue) {
            body.exclusive = true;
        }

        this._creatingDraftPromise = jsonFetch("/party_drafts", {
            method: "POST",
            setupRequiresAccount: !this.anonymousValue,
            body: JSON.stringify(body)
        }).then(res => {
            this._adoptDraftResponse(res);
            if (!this.draftId) throw new Error("Could not save your invitation draft. Please try again.");
        }).finally(() => { this._creatingDraftPromise = null; });
        return this._creatingDraftPromise;
    }

    async _autosaveJson(payload) {
        if (this._suppressAutosaveUntilInteraction || this._savingFinal || this._navigatingAway || this._editorDisconnected) return;
        const snapshot = JSON.parse(JSON.stringify(payload));
        const request = (this._saveQueue || Promise.resolve()).then(async () => {
            // Queued (not dispatched) snapshots are superseded by the final save.
            if (this._savingFinal || this._navigatingAway || this._editorDisconnected) return;
            if (this.contextValue === "party") {
                if (!this.hasPartyTokenValue || !this.partyTokenValue) throw new Error("Missing party token");
                await jsonFetch(`/party/${encodeURIComponent(this.partyTokenValue)}/edit/design`, {
                    method: "PATCH",
                    body: JSON.stringify({ editor_json: snapshot })
                });
            } else {
                await this._createOrReuseDraft(snapshot);
                if (!this.draftId) throw new Error("Missing invitation draft");
                await jsonFetch(`/party_drafts/${this.draftId}`, {
                    method: "PATCH",
                    body: JSON.stringify({ editor_json: snapshot })
                });
            }
            this._autosaveWarningShown = false;
        });
        // Keep the queue usable after a failure; final Save retries the full snapshot.
        this._saveQueue = request.catch(error => this._warnAutosaveFailure(error));
        return this._saveQueue;
    }

    _warnAutosaveFailure(error) {
        console.warn("Invitation autosave failed:", error);
        if (this._autosaveWarningShown || this._savingFinal || this._editorDisconnected) return;
        this._autosaveWarningShown = true;
        if (error.authenticationRequired) { this._showSetupSignIn(); return; }
        Swal.fire({
            toast: true, position: "bottom-end", icon: "warning", title: "Changes not saved yet",
            text: `Please stay on this page and use ${this.contextValue === "party" ? "Save changes" : "Continue"} to retry.`,
            showConfirmButton: true, confirmButtonText: "OK"
        });
    }

    async _showSetupSignIn() {
        const resumeContinue = this._savingFinal;
        if (!await signInForPartySetup(Swal) || this._editorDisconnected) return;
        await this._continuePromise;
        if (this._editorDisconnected) return;
        if (resumeContinue) return this._handleContinue();
        return this._autosaveJson(this._dumpJson());
    }

    _partyPlaceDraftContext() {
        const context = {}
        const rsvpStart = this.element?.dataset?.rsvpPartyStart
        if (rsvpStart) context.rsvp_start = rsvpStart
        const currentUrl = new URL(window.location.href)
        const allowed = [
            "back_qs", "utm_source", "utm_medium", "utm_campaign",
            "utm_content", "utm_term", "gclid", "fbclid"
        ]

        allowed.forEach((key) => {
            const value = currentUrl.searchParams.get(key)
            if (value) context[key] = value
        })

        if (this.hasVenuePartnerSlugValue && this.venuePartnerSlugValue) {
            context.venue = this.venuePartnerSlugValue
            context.entry_source = this.hasEntrySourceValue ? this.entrySourceValue : "party_places_partner"
        }

        if (this.hasCharityIdValue && this.charityIdValue) {
            context.charity_id = this.charityIdValue
        }
        if (this.hasExclusiveValue && this.exclusiveValue) {
            context.exclusive = true
        }

        return context
    }

    _adoptDraftResponse(res) {
        if (!res?.id) return
        this.draftId = String(res.id)
        if (res.draft_sgid) this._draftSgid = res.draft_sgid
        if (typeof res.anonymous === "boolean") this.anonymousValue = res.anonymous

        if (res.anonymous && window.history?.replaceState) {
            const url = new URL(window.location.href)
            url.searchParams.set("draft_ref", this.draftId)
            url.searchParams.delete("draft_id")
            url.searchParams.delete("draft_sgid")
            window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`)
        }
    }
    _dumpJson() {
        const photo = this.photoCarrier ? {
            imageId: this._photoImageId || null,
            x: this.photoCarrier.x(),
            y: this.photoCarrier.y(),
            scaleX: this.photoCarrier.scaleX() || 1,
            scaleY: this.photoCarrier.scaleY() || 1,
            rotation: this.photoCarrier.rotation() || 0,
            width: this.photoCarrier.width(),
            height: this.photoCarrier.height(),
            filter: this._photoFilter || ""
        } : null;
        const slotRect = this.photoSlot
            ? { x: this.photoSlot.x(), y: this.photoSlot.y(), w: this.photoSlot.width(), h: this.photoSlot.height() }
            : (this._savedPhotoSlotRect || null);
        const out = {
            base: {
                width: this.stage.width(),
                height: this.stage.height(),
                bgUrl: this.bgUrlValue, // you already pass it in as data-invitation-editor-bg-url-value
            },
            photoSlot: slotRect,
            photo: null,
            texts: [],
            stickers: [],
        }

        // Photo: if user uploaded
        if (this.photoNode) {
            out.photo = photo
        }

        // Text nodes: dump *all* text nodes on the content layer
        const textNodes = this.content?.find?.('Text') || [];
        for (let node of textNodes) {
            // Preserve semantic roles for the four main fields; everything else is a generic text node (duplicates)
            let key = null;
            if (node === this.titleNode) {
                key = 'title';
            } else if (node === this.whenNode) {
                key = 'when';
            } else if (node === this.timeNode) {
                key = 'time';
            } else if (node === this.whereNode) {
                key = 'where';
            } else {
                // fall back to node.name() (e.g. "text") or null
                key = typeof node.name === 'function' ? (node.name() || null) : null;
            }

            out.texts.push({
                key,
                text: node.text(),
                x: node.x(),
                y: node.y(),
                width: node.width(),
                scaleX: node.scaleX(),
                scaleY: node.scaleY(),
                rotation: node.rotation(),
                fontSize: node.fontSize(),
                fontFamily: node.fontFamily(),
                fontStyle: node.fontStyle(),
                fill: node.fill(),
                align: node.align(),
                lineHeight: node.lineHeight(),
                letterSpacing: node.letterSpacing?.(),
                padding: node.padding(),
            });
        }

        // Stickers: loop over children of stickers layer
        if (this.stickersLayer) {
            this.stickersLayer.find("Image").forEach(img => {
                out.stickers.push({
                    name: img.name() || "sticker",
                    src: img.image()?.src || null,
                    x: img.x(),
                    y: img.y(),
                    scaleX: img.scaleX(),
                    scaleY: img.scaleY(),
                    rotation: img.rotation(),
                    width: img.width(),
                    height: img.height()
                })
            })
        }

        return out
    }

    // Rehydrate everything from a previously saved dump
    async _loadJson(snapshot = {}) {
        if (!snapshot || typeof snapshot !== "object") return;

        // Normalize logical → digested sticker srcs
        if (Array.isArray(snapshot.stickers)) {
            snapshot.stickers.forEach(s => {
                if (s?.src && !/^https?:\/\//.test(s.src)) {
                    s.src = resolveStickerUrl(s.src);
                }
            });
        }

        // 1) Background
        try {
            const bgUrl = snapshot.base?.bgUrl || this.bgUrlValue;
            if (bgUrl) await this._loadBackground(bgUrl);
        } catch (e) {
            console.warn("BG load failed:", e);
        }

        if (this.transformer) {
            this.transformer.nodes([]);
        } else {
            // As a safeguard, if the transformer was somehow destroyed, recreate it here.
            this.transformer = new Konva.Transformer({
                rotateEnabled: true,
                enabledAnchors: ['middle-left','middle-right','top-left','top-right','bottom-left','bottom-right']
            });
            this.uiLayer.add(this.transformer);
        }

            // Clear content layers only
        this.content.destroyChildren();
        this.photoLayer?.destroyChildren();
        this.stickersLayer?.destroyChildren();

        // Ensure layers exist (in case)
        this.content?.draw();
        this.photoLayer?.draw();
        this.stickersLayer?.draw();
        this.uiLayer?.draw();

        // 3) Texts
        await this._rebuildTexts(snapshot.texts || []);

        // 4) Photo (slot first, then image)
        await this._rebuildPhoto(snapshot.photo || null);
        // 5) Stickers
        await this._rebuildStickers(snapshot.stickers || []);

        // 6) Select a sane default (title if present; else any text; else photo; else sticker)
        const prefer = (name) => this.content?.findOne(`.${name}`) || null;
        const title = prefer("title");
        const anyText = this.content?.findOne("Text");
        const anyPhoto = this.photoNode || null;
        const anySticker = this.stickersLayer?.findOne("Image") || null;
        const pick = title || anyText || anyPhoto || anySticker;

        if (pick) this._select(pick);
        this.stage?.draw();
        if (document.fonts?.ready) {
            document.fonts.ready.then(() => {
                if (!this.stage || this.stage.isDestroyed?.()) return;
                this._refreshTextFontRendering();
            });
        }
        // this._syncJson?.();
    }

    /* ---------- helpers ---------- */

// Rebuild Konva.Text nodes from JSON.
// Keys “title/when/where” will reuse your roles; others will still render.
    async _rebuildTexts(texts = []) {
        if (!texts || texts.length === 0) return;
        // --- START: NEW FONT PRELOADING LOGIC ---

        // 1. Find all unique font families from the saved JSON data.
        const uniqueFontFamilies = [...new Set(
            texts.map(textData => textData.fontFamily).filter(Boolean)
        )];

        // 2. Create an array of promises, one for each font that needs to be loaded.
        const fontLoadingPromises = uniqueFontFamilies.map(family => ensureFontLoaded(family));

        // 3. Wait for all the required fonts to be loaded in parallel.
        try {
            await Promise.all(fontLoadingPromises);
        } catch (e) {
            console.warn("Some fonts could not be loaded:", e);
        }

        // --- END: NEW FONT PRELOADING LOGIC ---
        const knownByKey = {};
        for (const t of texts) {
            const opts = {
                text: t.text || "",
                x: t.x ?? 0,
                y: t.y ?? 0,
                rotation: t.rotation || 0,
                fontSize: t.fontSize || 24,
                fontFamily: t.fontFamily || "Arial",
                fontStyle: t.fontWeight || t.fontStyle || "normal",
                fill: t.fill || "#1a2d70",
                align: t.align || "center",
                lineHeight: t.lineHeight || 1.2,
                letterSpacing: t.letterSpacing ?? 0,
                padding: t.padding ?? 0,
                width: t.width || Math.round(this.stage.width() * 0.86),
                draggable: true,
                listening: true,
                name: t.key || "text"
            };

            // Use your factory so everything (frames, handlers) stays consistent
            const node = this._makeText(opts.text, opts, t.key || "text");

            // If you keep special pointers, remember them
            if (t.key === "title")   this.titleNode = node;
            if (t.key === "when")    this.whenNode  = node;
            if (t.key === "where")   this.whereNode = node;
            if (t.key === "time")    this.timeNode = node;
            knownByKey[t.key || `text-${Date.now()}`] = node;

            this.content.add(node);
            this._attachSelectable(node);
            this._ensureFrameFor?.(node);
            this._updateFrame?.(node);
        }

        this.content?.batchDraw();
        return knownByKey;
    }

// Rebuild the photo inside your clipped slot group
    async _rebuildPhoto(photo) {
        const slot = this._ensurePhotoSlot?.();
        if (!slot) return;

        if (!photo || !(photo.imageId || photo.url)) {
            this._showSlotPrompt?.();
            this.photoLayer?.batchDraw();
            return;
        }
        this._hideSlotPrompt?.();
        if (this.photoCarrier) { this.photoCarrier.destroy(); this.photoCarrier = null; }
        if (this.photoNode)    { this.photoNode.destroy();    this.photoNode = null;    }

        const HASH = window.CF_DELIVERY_HASH || document.querySelector('meta[name="cf-delivery-hash"]')?.content || "";
        const srcUrl = photo.url || (HASH ? `https://imagedelivery.net/${HASH}/${photo.imageId}/public` : null);
        if (!srcUrl) { this._showSlotPrompt?.(); return; }

        const img = await loadImage(srcUrl).catch(() => null);
        if (!img) { this._showSlotPrompt?.(); return; }

        this._photoImageId = photo.imageId || null;
        this._photoUrl = srcUrl;

        this.photoCarrier = new Konva.Group({ draggable: true, listening: true, name: "photoCarrier" });
        slot.add(this.photoCarrier);

        this.photoNode = new Konva.Image({ image: img, draggable: false, listening: true, name: "photoNode" });
        this.photoCarrier.add(this.photoNode);

        let targetW, targetH;

        if (photo.width && photo.height) {
            targetW = photo.width; targetH = photo.height;
        } else {
            const fit = this._fitCover(
                img.naturalWidth || img.width,
                img.naturalHeight || img.height,
                slot.width(),
                slot.height()
            );
            targetW = fit.w; targetH = fit.h;
        }

        // Reset scale before sizing
        this.photoCarrier.scale({ x: 1, y: 1 });
        this.photoNode.size({ width: targetW, height: targetH });
        this.photoCarrier.size({ width: targetW, height: targetH });

        // Now that sizing is final, apply any saved filter
        this._photoFilter = photo.filter || "";
        this._setPhotoFilterLocal(this._photoFilter);

        // Apply saved scale, then position and rotation last
        const sx = (typeof photo.scaleX === 'number' ? photo.scaleX : 1) || 1;
        const sy = (typeof photo.scaleY === 'number' ? photo.scaleY : 1) || 1;
        this.photoCarrier.scale({ x: sx, y: sy });

        const px = (typeof photo.x === 'number') ? photo.x : 0;
        const py = (typeof photo.y === 'number') ? photo.y : 0;
        this.photoCarrier.position({ x: px, y: py });

        if (typeof photo.rotation === 'number') this.photoCarrier.rotation(photo.rotation);

        this._attachPhotoListeners();

        this._select(this.photoCarrier);
        document.dispatchEvent(new CustomEvent("ie:inspect", { detail: { kind: "Photo" } }));

        this.photoLayer?.batchDraw();

        this._syncPhotoUi();
    }

    // Decide whether the detected hole looks circular or rectangular
    // by sampling the fraction of hole pixels inside its bounding box.
    // Circles cover ~π/4 ≈ 0.785 of their bounding box; rounded rects are closer to 1.
    _inferHoleShape(hole) {
        try {
            const s = this._bgSample;
            if (!s || !s.holeMask || !hole) return { circle: false };
            const { CW, CH, holeMask } = s;
            const { x, y, w, h } = hole;

            // Map stage-space rect -> sample-space rect
            const sx = Math.max(0, Math.floor(x * CW / BASE_W));
            const sy = Math.max(0, Math.floor(y * CH / BASE_H));
            const sw = Math.max(1, Math.floor(w * CW / BASE_W));
            const sh = Math.max(1, Math.floor(h * CH / BASE_H));

            let probes = 0, hits = 0;
            const STEP = Math.max(2, Math.floor(Math.min(sw, sh) / 20));
            for (let yy = sy; yy < Math.min(CH, sy + sh); yy += STEP) {
                for (let xx = sx; xx < Math.min(CW, sx + sw); xx += STEP) {
                    probes++;
                    if (holeMask?.[yy]?.[xx]) hits++;
                }
            }
            if (!probes) return { circle: false };
            const frac = hits / probes; // 0..1
            // Heuristic: if fill fraction is clearly below ~0.88, treat as circle; otherwise rect.
            return { circle: frac < 0.88 };
        } catch (_) {
            return { circle: false };
        }
    }

// Rebuild stickers (regular Konva.Image children on stickerLayer)
    async _rebuildStickers(stickers) {
        const tasks = stickers.map(async s => {
            if (!s.src) return null;
            const img = await loadImage(s.src).catch(e => (console.warn("Sticker load failed:", e), null));
            if (!img) return null;

            const node = new Konva.Image({
                image: img,
                name: s.name || "sticker",
                x: s.x ?? 0,
                y: s.y ?? 0,
                scaleX: s.scaleX || 1,
                scaleY: s.scaleY || 1,
                rotation: s.rotation || 0,
                draggable: true,
                listening: true
            });
            if (s.width && s.height) node.size({ width: s.width, height: s.height });
            node.position({ x: s.x || 0, y: s.y || 0 });
            node.rotation(s.rotation || 0);
            node.scale({ x: s.scaleX || 1, y: s.scaleY || 1 });
            node.name('sticker');

            this.stickersLayer.add(node);

            // Use the new centralized function for all listeners
            this._attachInteractionListeners(node);

            return node;
        });

        await Promise.all(tasks);
        this.stickersLayer?.batchDraw();
    }


    // --- Text node helpers ---

    _wireTextNode(node) {
        if (!node || node.getClassName?.() !== 'Text') return;
        try { node.draggable(true); } catch(_) {}
        try { node.listening(true); } catch(_) {}
        this._attachSelectable(node);
        this._ensureFrameFor(node);
        node.off('dblclick dbltap');
        node.on('dblclick dbltap', () => this._inlineEdit(node));

        const onTransform = () => {
            // live frame refresh while dragging corners
            this._updateFrame(node);
        };
        const onTransformEnd = () => {
            // convert scale -> real geometry so JSON persists correctly
            const sx = node.scaleX() || 1;
            const sy = node.scaleY() || 1;
            if (sx !== 1) {
                node.width(Math.max(20, node.width() * sx));
                node.scaleX(1);
            }
            if (sy !== 1) {
                node.fontSize(Math.max(6, node.fontSize() * sy));
                node.scaleY(1);
            }
            node.setAttr('_boxWidth', node.width());
            this._updateFrame(node);
            this.content.batchDraw();
            this._syncJson();
        };
        node.off('transform transformend');
        node.on('transform', onTransform);
        node.on('transformend', onTransformEnd);
        node.off('dragend');
        node.on('dragend', () => { this._updateFrame(node); this._syncJson(); });
    }

    _wireAllTextNodes() {
        const texts = this.content?.find?.('Text') || [];
        texts.forEach(t => this._wireTextNode(t));
        // Try to restore role handles if missing
        const byId = (id) => texts.find(t => t.id?.() === id);
        this.titleNode ||= byId('title');
        this.whenNode  ||= byId('when');
        this.whereNode ||= byId('where');
        this.timeNode ||=  byId('time');
    }

    _normalizeTextTransform(node) {
        if (!node || node.getClassName?.() !== 'Text') return;
        const sx = node.scaleX() || 1;
        const sy = node.scaleY() || 1;
        if (sx !== 1) {
            node.width(Math.max(20, node.width() * sx));
            node.scaleX(1);
        }
        if (sy !== 1) {
            node.fontSize(Math.max(6, node.fontSize() * sy));
            node.scaleY(1);
        }
        node.setAttr('_boxWidth', node.width());
    }
    //* Attaches all necessary interaction event listeners to a node.
    //* This is the single source of truth for selecting, dragging, and transforming.

    _attachInteractionListeners(node) {
        // Clear any previous listeners to prevent duplicates
        node.off('click tap dragstart dragmove dragend transformstart transform transformend');

        // --- Selection ---
        node.on("click tap", (e) => {
            // Stop the event from bubbling up to the stage's click listener
            e.cancelBubble = true;
            this._select(node);
        });

        // --- Drag & Transform Handlers ---
        node.on('dragstart transformstart', () => {
            this._showMiniToolbarFor(node);
        });

        node.on('dragmove transform', () => {
            // This is optional, but can feel more responsive if the toolbar
            // needs to follow the node during the action.
            this._showMiniToolbarFor(node);
        });

        node.on('dragend transformend', () => {
            // After the action, ensure the node's cache is clean and save the state.
            node.clearCache();
            this._showMiniToolbarFor(node); // Reposition toolbar at final location
            this._syncJson();
        });
    }

} // End of class

// Helpers
function setValueIfExists(sel, val) {
    const el = document.querySelector(sel)
    if (el) el.value = val
}

function fileToDataURL(file, maxDim = 2000) {
    return new Promise((res, rej) => {
        const fr = new FileReader();
        fr.onload = () => {
            const img = new Image();
            img.onload = () => {
                let { width, height } = img;
                if (width > maxDim || height > maxDim) {
                    const scale = maxDim / Math.max(width, height);
                    const canvas = document.createElement("canvas");
                    canvas.width = Math.round(width * scale);
                    canvas.height = Math.round(height * scale);
                    const ctx = canvas.getContext("2d");
                    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                    res(canvas.toDataURL("image/jpeg", 0.92)); // compressed + scaled
                } else {
                    res(fr.result); // already small enough
                }
            };
            img.onerror = rej;
            img.src = fr.result;
        };
        fr.onerror = rej;
        fr.readAsDataURL(file);
    });
}
function loadImage(src) {
    return new Promise((res, rej) => {
        const img = new Image()
        img.crossOrigin = "anonymous"
        img.referrerPolicy = "no-referrer";  // ← helps with strict CDNs
        img.onload = () => res(img);
        img.onerror = (e) => { console.error("Image load failed:", src, e); rej(e); };
        img.src = src;
    })
}

const CASE_FN = {
    uppercase: (s) => s.toUpperCase(),
    lowercase: (s) => s.toLowerCase(),
    capitalize: (s) =>
        s.replace(/\b\w/g, (m) => m.toUpperCase()).replace(/\B\w/g, (m) => m.toLowerCase())
}
function normalizeStickerSrc(url) {
    // If it is a logical path under stickers with extension, keep it
    const m = url.match(/stickers\/[\w\-\/]+\.(png|webp|jpg|jpeg)$/i);
    if (m) return `stickers/${m[0].split('stickers/')[1]}`;

    // Digested asset like /assets/stickers/cat-abc123.png
    const dig = url.match(/\/assets\/(stickers\/[^?]+?)(?:-[0-9a-f]{8,}\.(png|webp|jpg|jpeg))?(?:\?v=\d+)?$/i);
    if (dig) return dig[1];

    // If given like "stickers/cat" (no ext) assume .png
    if (/^stickers\//.test(url) && !/\.(png|webp|jpg|jpeg)$/i.test(url)) return `${url}.png`;

    return url;
}

// Reuse + extend your existing sanitizer
function _normalizeForSave(payload) {
    // Shallow clone if you prefer not to mutate the original:
    // const j = JSON.parse(JSON.stringify(payload));
    const j = payload;

    // 1) Stickers: keep digested/normalized src only (your existing logic)
    if (j?.stickers) {
        j.stickers.forEach(s => {
            if (s.src) s.src = normalizeStickerSrc(s.src);
        });
    }

    // 2) Photo: never save blobs; keep only id + transforms
    if (j?.photo) {
        // Drop any base64 or oversized URL fields
        if ('src' in j.photo) delete j.photo.src;
        if (j.photo.url && String(j.photo.url).startsWith('data:')) delete j.photo.url;

        // If we have an imageId, that’s all we need (+ geometry).
        // Optionally hard-enforce the allowed keys:
        const allowed = ['imageId', 'x', 'y', 'scaleX', 'scaleY', 'rotation', 'width', 'height', 'url', 'filter'];
        Object.keys(j.photo).forEach(k => {
            if (!allowed.includes(k)) delete j.photo[k];
        });

        // If neither imageId nor url remain (e.g., just deleted), normalize to null
        if (!j.photo.imageId && !j.photo.url) {
            j.photo = null;
        }
    }

    // 3) (Optional) round noisy floats to shrink payload a bit
    const round2 = (v) => (typeof v === 'number' ? Math.round(v * 100) / 100 : v);
    if (j?.photo) {
        ['x','y','scaleX','scaleY','rotation','width','height'].forEach(k => { j.photo[k] = round2(j.photo[k]); });
    }
    if (j?.texts) {
        j.texts.forEach(t => {
            ['x','y','rotation','width','fontSize','letterSpacing','lineHeight','padding'].forEach(k => { if (k in t) t[k] = round2(t[k]); });
        });
    }
    if (j?.stickers) {
        j.stickers.forEach(s => {
            ['x','y','scaleX','scaleY','rotation','width','height'].forEach(k => { if (k in s) s[k] = round2(s[k]); });
        });
    }

    return j;
}
